import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  doc, getDoc, updateDoc, collection, getDocs, addDoc, deleteDoc, query, orderBy, where
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, Grid, MenuItem, Divider, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Alert,
  Switch
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import { deptCode } from '../utils/depts';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GavelIcon from '@mui/icons-material/Gavel';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { enrichPerson, mapPersonToContact, enrichCompany, mapCompanyToForm, enrichCompanyEmployees } from '../services/enrichLayer';
import { sparaKundlogga, sparaKontaktfoto } from '../modules/crm/bildkopia';
import AddressAutocomplete from '../components/AddressAutocomplete';
import CopyCustomerDataDialog from '../modules/crm/CopyCustomerDataDialog';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { authFetch } from '../utils/authFetch';
import KopieraKnapp from '../modules/crm/KopieraKnapp';
import { SegmentValjare, OrganisationstypValjare, SegmentEtikett } from '../modules/crm/KategoriVal';
import { tommaFaltFranBolagsverket } from '../modules/crm/kundformular';
import { hamtaBolag } from '../modules/crm/bokningsmailData';
import LinkedInHamtning, { kontaktFaltFranLinkedIn, kundFaltFranLinkedIn } from '../modules/crm/LinkedInHamtning';
import KundprojektKnapp from '../modules/crm/KundprojektKnapp';
import { useSignaturTolkning, SignaturStatus } from '../modules/crm/SignaturTolkning';
import { KundSakerhetFlaggor, KundprojektLista, KontaktKundprojekt } from '../modules/crm/KundprojektVyer';
import { laddaKundprojektMedIds, laddaLeadsMedIds } from '../modules/crm/kundprojektData';
import ConstructionIcon from '@mui/icons-material/Construction';

const COMPANY_STATUSES = [
  { key: 'prospect',  label: 'Prospect',     color: '#1565C0', bg: '#E3F2FD' },
  { key: 'aktiv',     label: 'Aktiv kund',   color: '#2E7D32', bg: '#E8F5E9' },
  { key: 'vilande',   label: 'Vilande',      color: '#795548', bg: '#EFEBE9' },
];


const EMPTY_CONTACT = {
  name: '', role: '', department: '', email: '', phone: '', mobile: '', linkedin: '',
  primary: false, dealRelation: '', notes: '', location: '', summary: '',
  linkedinHeadline: '', currentCompany: '', education: '', languages: '',
  photoUrl: '', experience: '', certifications: '', skills: '',
};

const EMPTY_REF = {
  name: '', role: '', startDate: '', endDate: '', budget: '',
  projectDescription: '', ourAssignment: '', taskList: [], keywords: [],
  departmentId: '',
};

const EMP_SENIOR_KEYWORDS = ['ceo','cto','cfo','coo','cso','chief','vd','direktör','director','managing',
  'partner','president','vice president','vp ','head of','chef','ledare','styrelseordförande',
  'chairman','founder','co-founder','grundare','ägare','owner','senior','lead','principal','architect'];

function initials(name = '') {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function FieldRow({ label, value, href, kopiera }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1.5, mb: 0.75, alignItems: 'flex-start' }}>
      <Typography variant="caption" color="text.secondary"
        sx={{ width: 120, flexShrink: 0, pt: 0.1, fontWeight: 500 }}>{label}</Typography>
      {href ? (
        <Typography variant="caption" component="a" href={href}
          target="_blank" rel="noopener noreferrer"
          sx={{ color: '#FF6B00', textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' } }}>
          {value}
        </Typography>
      ) : (
        <Typography variant="caption" fontWeight={500}>{value}</Typography>
      )}
      {kopiera && <KopieraKnapp text={value} size={11} sx={{ mt: -0.25 }} />}
    </Box>
  );
}

function companyStatusInfo(key) {
  return COMPANY_STATUSES.find(s => s.key === key) || null;
}

function parseContactFromPaste(str) {
  if (!str?.trim()) return null;
  const lines = str.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const emailRe = /[\w.+\-]+@[\w-]+\.[\w.]+/i; // eslint-disable-line no-useless-escape
  const phoneRe = /(\+?[0-9][0-9 ()\-]{6,}[0-9])/; // eslint-disable-line no-useless-escape
  let name = '', email = '', phone = '';
  const singleLine = str.trim();
  const angleMatch = singleLine.match(/^(.+?)\s*<([^>]+)>\s*$/);
  if (angleMatch) {
    const possibleName = angleMatch[1].trim();
    const possibleEmail = angleMatch[2].trim().toLowerCase();
    email = possibleEmail;
    name = possibleName.includes('@') ? '' : possibleName;
    return { name, email, phone };
  }
  for (const line of lines) {
    if (!email) { const em = line.match(emailRe); if (em) { email = em[0].toLowerCase(); continue; } }
    if (!phone) { const ph = line.match(phoneRe); if (ph) { phone = ph[1].replace(/\s+/g, ' ').trim(); continue; } }
    if (!name && !line.includes('@') && !line.match(phoneRe) && line.length > 1 && line.length < 60) name = line;
  }
  return (name || email) ? { name, email, phone } : null;
}

// Plockar ut tio siffror ur ett inklistrat värde. Tar hand om mellanslag,
// tankstreck, punkter och prefix som "Org.nr:", och kortar sekelprefixet 16.
function orgNrDigits(value) {
  let d = String(value || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('16')) d = d.slice(2);
  return /^\d{10}$/.test(d) ? d : '';
}

// Svenska organisationsnummer har en Luhn-kontrollsiffra sist. BolagsAPI avvisar
// nummer som inte stämmer, så det är bättre att fånga det här och säga varför än
// att skicka iväg ett anrop som ändå nekas.
function orgNrChecksumOk(d) {
  let sum = 0;
  for (let i = 0; i < d.length; i++) {
    let n = Number(d[i]);
    if (i % 2 === 0) n *= 2;
    if (n > 9) n -= 9;
    sum += n;
  }
  return sum % 10 === 0;
}

// Vilken sista siffra numret borde haft — gör felet åtgärdbart i stället för
// bara konstaterat.
function orgNrExpectedLastDigit(d) {
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let n = Number(d[i]);
    if (i % 2 === 0) n *= 2;
    if (n > 9) n -= 9;
    sum += n;
  }
  return (10 - sum % 10) % 10;
}

// Returnerar ett giltigt 10-siffrigt org.nr, eller '' om värdet inte är ett.
// Kontrollsiffran ingår i kravet — annars släpps kundnummer som råkar ha tio
// siffror igenom snabbfyllet från bokningsmailet.
function normalizeOrgNr(value) {
  const d = orgNrDigits(value);
  return d && orgNrChecksumOk(d) ? d : '';
}

// Returnerar ett felmeddelande, eller '' om numret ser korrekt ut.
function orgNrProblem(raw) {
  const d = orgNrDigits(raw);
  if (!d) {
    return `"${String(raw).trim()}" är inget giltigt org.nr — ange 10 siffror, t.ex. 556000-0000`;
  }
  if (!orgNrChecksumOk(d)) {
    const fixed = d.slice(0, 9) + orgNrExpectedLastDigit(d);
    return `${d.slice(0, 6)}-${d.slice(6)} har fel kontrollsiffra. `
      + `Med samma första nio siffror skulle numret vara ${fixed.slice(0, 6)}-${fixed.slice(6)}.`;
  }
  return '';
}

// Kontakter som kommer in via Comdevas bokningsmail har alltid samma härkomst.
const COMDEVA_RELATION = 'Uppringd av Comdeva';

function parseBookingEmail(str) {
  if (!str?.trim()) return null;
  const lines = str.split(/\r?\n/).map(l => l.trim());
  const emailRe = /[\w.+\-]+@[\w-]+\.[\w.]+/i; // eslint-disable-line no-useless-escape
  const phoneRe = /(\+?[0-9][0-9 ()\-]{6,}[0-9])/; // eslint-disable-line no-useless-escape
  const LABELS = new Set(['Start','Slut','Företag/Person','Kundnummer','Ort',
    'Antal anställda','Bransch','Omsättning','Plats','Ämne','Beskrivning','Resurs']);
  if (!lines.some(l => l === 'Företag/Person' || l === 'Kundnummer' || l === 'Beskrivning')) return null;
  const r = { companyName:'', orgNumber:'', city:'', employees:'', industry:'', revenue:'',
    contactName:'', contactRole:'', contactPhone:'', contactEmail:'', meetingStart:'', meetingNotes:'' };
  let i = 0, beskrivIdx = -1, motesIdx = -1;
  while (i < lines.length) {
    const l = lines[i], v = lines[i + 1] || '';
    if (l === 'Start') { r.meetingStart = v.replace(/\s*On\s*$/, '').trim(); i += 2; continue; }
    if (l === 'Företag/Person') { r.companyName = v; i += 2; continue; }
    // Kundnummer från säljsystemet är inte alltid ett organisationsnummer –
    // fyll bara i org.nr-fältet när värdet faktiskt ser ut som ett sådant.
    if (l === 'Kundnummer') { r.orgNumber = normalizeOrgNr(v); i += 2; continue; }
    if (l === 'Ort') { r.city = v; i += 2; continue; }
    if (l === 'Antal anställda') { r.employees = v; i += 2; continue; }
    if (l === 'Bransch') { r.industry = v; i += 2; continue; }
    if (l === 'Omsättning') { r.revenue = v; i += 2; continue; }
    if (l === 'Beskrivning') { beskrivIdx = i + 1; i++; continue; }
    if (l.startsWith('Möteskommentar')) { motesIdx = i; i++; continue; }
    i++;
  }
  if (beskrivIdx >= 0) {
    const end = motesIdx >= 0 ? motesIdx : lines.length;
    for (const line of lines.slice(beskrivIdx, end).filter(Boolean)) {
      if (LABELS.has(line)) break;
      if (!r.contactEmail) { const m = line.match(emailRe); if (m) { r.contactEmail = m[0].toLowerCase(); continue; } }
      if (!r.contactPhone) { const m = line.match(phoneRe); if (m) { r.contactPhone = m[1].replace(/\s+/g, ' ').trim(); continue; } }
      if (!r.contactName && !line.includes('@') && line.length > 1 && line.length < 60) { r.contactName = line; continue; }
      if (r.contactName && !r.contactRole && !line.includes('@') && line.length > 1 && line.length < 60) { r.contactRole = line; continue; }
    }
  }
  if (motesIdx >= 0) {
    const noteLines = lines.slice(motesIdx + 1);
    const stop = noteLines.findIndex(l => l.startsWith('Bokad tid:') || l === 'Resurs');
    r.meetingNotes = (stop >= 0 ? noteLines.slice(0, stop) : noteLines).filter(Boolean).join('\n').trim();
  }
  return r;
}

function formatOrgNumber(val) {
  if (!val) return '';
  const digits = val.replace(/\D/g, '');
  if (digits.length === 10) return digits.slice(0, 6) + '-' + digits.slice(6);
  return val;
}

export default function CRMCompany() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [company, setCompany] = useState(null);
  // Kundens kundprojekt och leadsen de är kopplade till (för titlarna).
  const [kundprojekt, setKundprojekt] = useState([]);
  const [kpLeads, setKpLeads] = useState([]);
  const [kpLaddar, setKpLaddar] = useState(true);
  // AI-tolkning av en inklistrad mailsignatur i "Ny kontakt".
  const signatur = useSignaturTolkning(company);
  const [contacts, setContacts] = useState([]);
  const [copyCustomerOpen, setCopyCustomerOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [contactDialog, setContactDialog] = useState(false);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [editingContactId, setEditingContactId] = useState(null);
  const [enrichingContact, setEnrichingContact] = useState(false);
  const [enrichContactMsg, setEnrichContactMsg] = useState('');
  const [enrichingCompany, setEnrichingCompany] = useState(false);
  const [enrichCompanyMsg, setEnrichCompanyMsg] = useState('');
  const [references, setReferences] = useState([]);
  const [refDialog, setRefDialog] = useState(false);
  const [refForm, setRefForm] = useState(EMPTY_REF);
  const [editingRefId, setEditingRefId] = useState(null);
  const [refTagInput, setRefTagInput] = useState('');
  const [mapCoords, setMapCoords] = useState(null);
  const [formMapCoords, setFormMapCoords] = useState(null);
  const formGeoRef = React.useRef(null);
  const [contactPaste, setContactPaste] = useState('');
  const [parsedCompanyFromPaste, setParsedCompanyFromPaste] = useState(null);
  const [bulkDialog,  setBulkDialog]  = useState(false);
  const [bulkText,    setBulkText]    = useState('');
  const [bulkParsed,  setBulkParsed]  = useState([]);
  const [bulkAdding,  setBulkAdding]  = useState(false);

  // Medarbetare från LinkedIn
  const [empDialog,   setEmpDialog]   = useState(false);
  const [empLoading,  setEmpLoading]  = useState(false);
  const [empList,     setEmpList]     = useState([]);
  const [empNextPage, setEmpNextPage] = useState(null);
  const [empSelected, setEmpSelected] = useState(new Set());
  const [empAdding,   setEmpAdding]   = useState(false);
  const [empError,    setEmpError]    = useState('');
  const [empSearch,      setEmpSearch]      = useState('');
  const [empSort,        setEmpSort]        = useState('senior');
  const [empPreSearch,   setEmpPreSearch]   = useState('');

  const [lookingUp, setLookingUp] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [financials, setFinancials] = useState(null);
  const [loadingFinancials, setLoadingFinancials] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [linkedContracts, setLinkedContracts] = useState([]);
  const [descExpanded, setDescExpanded] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);

  const filteredEmployees = useMemo(() => {
    const score = emp => {
      const t = (emp.profile?.experiences?.[0]?.title || emp.profile?.occupation || emp.profile?.headline || '').toLowerCase();
      const idx = EMP_SENIOR_KEYWORDS.findIndex(k => t.includes(k));
      return idx === -1 ? 999 : idx;
    };
    return empList
      .filter(emp => {
        if (!empSearch.trim()) return true;
        const q = empSearch.toLowerCase();
        const p = emp.profile || {};
        const fullName2 = p.full_name || [emp.first_name, emp.last_name].filter(Boolean).join(' ') || '';
        return fullName2.toLowerCase().includes(q) ||
               (emp.title || '').toLowerCase().includes(q) ||
               (p.occupation || '').toLowerCase().includes(q) ||
               (p.experiences?.[0]?.title || '').toLowerCase().includes(q) ||
               (p.headline || '').toLowerCase().includes(q);
      })
      .slice()
      .sort((a, b) => {
        if (empSort === 'name') return (a.profile?.full_name || '').localeCompare(b.profile?.full_name || '', 'sv');
        if (empSort === 'senior') return score(a) - score(b);
        return 0;
      });
  }, [empList, empSearch, empSort]);

  useEffect(() => { load(); }, [companyId]); // eslint-disable-line

  useEffect(() => {
    if (!company) return;
    // Besöksadress: eget postnr/ort om angivet, annars fall back på postadress
    const street = company.visitStreet || company.address;
    const zip    = company.visitZip  || company.zip;
    const city   = company.visitCity || company.city;
    const q = [street, zip, city].filter(Boolean).join(', ');
    if (!q) { setMapCoords(null); return; }
    fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q + ', Sverige')}&format=json&limit=1`,
      { headers: { 'Accept-Language': 'sv', 'User-Agent': 'lagbasn-crm/1.0' } }
    )
      .then(r => r.json())
      .then(data => {
        if (data[0]) setMapCoords({ lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) });
        else setMapCoords(null);
      })
      .catch(() => setMapCoords(null));
  }, [company?.visitStreet, company?.visitZip, company?.visitCity, company?.address, company?.zip, company?.city]); // eslint-disable-line

  // Live-karta i redigeringsläget — geocodar form-adressen med debounce
  useEffect(() => {
    clearTimeout(formGeoRef.current);
    const street = form.visitStreet || form.address || '';
    const zip    = form.visitZip  || form.zip  || '';
    const city   = form.visitCity || form.city || '';
    const q = [street, zip, city].filter(Boolean).join(', ');
    if (!q || !editing) { setFormMapCoords(null); return; }
    formGeoRef.current = setTimeout(() => {
      fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q + ', Sverige')}&format=json&limit=1`,
        { headers: { 'Accept-Language': 'sv', 'User-Agent': 'lagbasn-crm/1.0' } }
      )
        .then(r => r.json())
        .then(data => {
          if (data[0]) setFormMapCoords({ lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) });
          else setFormMapCoords(null);
        })
        .catch(() => setFormMapCoords(null));
    }, 800);
  }, [form.visitStreet, form.visitZip, form.visitCity, form.address, form.zip, form.city, editing]); // eslint-disable-line

  async function load() {
    setLoading(true);
    const [compSnap, deptSnap, contactsSnap, refsSnap, prodSnap, contractsSnap] = await Promise.all([
      getDoc(doc(db, 'crmCompanies', companyId)),
      getDocs(collection(db, 'departments')),
      getDocs(query(collection(db, 'crmCompanies', companyId, 'contacts'), orderBy('name'))),
      getDocs(query(collection(db, 'crmCompanies', companyId, 'references'), orderBy('startDate', 'desc'))),
      getDocs(query(collection(db, 'products'), orderBy('name'))),
      // Avtal läses bara av anställda (inte inbjudna externa konsulter) –
      // en nekad fråga ger en tom lista i stället för att sidan fallerar
      getDocs(query(collection(db, 'contracts'), where('counterpartyId', '==', companyId)))
        .catch(() => ({ docs: [] })),
    ]);
    if (compSnap.exists()) {
      const raw = { id: compSnap.id, ...compSnap.data() };
      // Normalize: old companies stored LinkedIn as 'linkedinUrl', new ones use 'linkedin'
      const data = { ...raw, linkedin: raw.linkedin || raw.linkedinUrl || '' };
      setCompany(data);
      setForm(data);
      laddaKundensKundprojekt(data.kundprojektIds);
    }
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setContacts(contactsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setReferences(refsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setAllProducts(prodSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLinkedContracts(contractsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  async function handleSaveRef() {
    setSaving(true);
    const { id, ...data } = refForm;
    if (editingRefId) {
      await updateDoc(doc(db, 'crmCompanies', companyId, 'references', editingRefId), data);
      setReferences(prev => prev.map(r => r.id === editingRefId ? { id: editingRefId, ...data } : r));
    } else {
      const docRef = await addDoc(collection(db, 'crmCompanies', companyId, 'references'), data);
      setReferences(prev => [{ id: docRef.id, ...data }, ...prev]);
    }
    setRefDialog(false);
    setSaving(false);
  }

  async function handleDeleteRef(refId) {
    await deleteDoc(doc(db, 'crmCompanies', companyId, 'references', refId));
    setReferences(prev => prev.filter(r => r.id !== refId));
  }

  function openNewRef() {
    setRefForm(EMPTY_REF);
    setEditingRefId(null);
    setRefTagInput('');
    setRefDialog(true);
  }

  function openEditRef(ref) {
    setRefForm({ ...ref });
    setEditingRefId(ref.id);
    setRefTagInput('');
    setRefDialog(true);
  }

  // Berikning efter att org.nr fyllts i: fråga → hämta → visa fälten → fyll i.
  // Bara tomma fält (tommaFaltFranBolagsverket); inget skrivs utan bekräftelse.
  const [berikning, setBerikning] = useState(null); // null | { steg: 'fraga'|'hamtar'|'visa'|'fel', falt, fel }
  async function hamtaBerikning() {
    setBerikning({ steg: 'hamtar' });
    try {
      const data = await hamtaBolag(normalizeOrgNr(company.orgNumber));
      setBerikning({ steg: 'visa', falt: tommaFaltFranBolagsverket(company, data) });
    } catch (e) {
      setBerikning({ steg: 'fel', fel: e.message || String(e) });
    }
  }
  async function sparaBerikning() {
    const falt = berikning.falt;
    await updateDoc(doc(db, 'crmCompanies', companyId), { ...falt, updatedAt: new Date() });
    setCompany(c => ({ ...c, ...falt }));
    setForm(f => ({ ...f, ...falt }));
    setBerikning(null);
  }

  async function handleSaveCompany() {
    setSaving(true);
    try {
      const { id, ...data } = form;
      // Firestore accepterar inte undefined-värden
      const clean = Object.fromEntries(
        Object.entries(data).filter(([, v]) => v !== undefined)
      );
      await updateDoc(doc(db, 'crmCompanies', companyId), { ...clean, updatedAt: new Date() });
      sparaKundlogga(companyId, clean); // i bakgrunden
      // Org.nr ifyllt på en kund som saknade det: erbjud berikning från Bolagsverket
      const fickOrgNr = !company?.orgNumber && form.orgNumber && !orgNrProblem(form.orgNumber);
      setCompany(form);
      setEditing(false);
      if (fickOrgNr) setBerikning({ steg: 'fraga' });
    } catch (e) {
      alert('Kunde inte spara: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  // LinkedIn-adress inklistrad på kundkortet: hämtat sparas direkt.
  async function sparaKundLinkedIn(mapped, url) {
    const falt = kundFaltFranLinkedIn(company, mapped, url);
    await updateDoc(doc(db, 'crmCompanies', companyId), { ...falt, updatedAt: new Date() });
    sparaKundlogga(companyId, { ...company, ...falt }); // i bakgrunden
    setCompany(c => ({ ...c, ...falt }));
    setForm(f => ({ ...f, ...falt }));
  }

  async function laddaKundensKundprojekt(ids) {
    setKpLaddar(true);
    try {
      const kps = await laddaKundprojektMedIds(ids);
      setKundprojekt(kps);
      const leadIds = kps.flatMap(kp => (kp.parter || []).filter(p => p.kundId === companyId).flatMap(p => p.leadIds || []));
      setKpLeads(leadIds.length ? await laddaLeadsMedIds(leadIds) : []);
    } catch (e) {
      console.warn('Kunde inte läsa kundprojekt', e);
    } finally {
      setKpLaddar(false);
    }
  }

  const upsertKp = (lista, kp) => (lista.some(k => k.id === kp.id) ? lista.map(k => (k.id === kp.id ? kp : k)) : [...lista, kp]);

  // Nytt kundprojekt: håll kundens och kontaktens avledda kundprojektIds och
  // kundflaggor aktuella lokalt (Firestore är redan uppdaterat).
  function kundprojektSparat({ id, flaggor, andringar, kundprojekt: kp }) {
    const borta = andringar.kunder.ta.includes(companyId);
    setKundprojekt(prev => (borta ? prev.filter(k => k.id !== id) : upsertKp(prev, kp)));
    const med = list => [...new Set([...(list || []), id])];
    const utan = list => (list || []).filter(x => x !== id);
    setCompany(c => ({ ...c, kundprojektIds: borta ? utan(c.kundprojektIds) : med(c.kundprojektIds), ...(flaggor[companyId] || {}) }));
    const hit = new Set(andringar.kontakter.lagg.map(n => n.split('/')[1]));
    const dit = new Set(andringar.kontakter.ta.map(n => n.split('/')[1]));
    setContacts(prev => prev.map(c => (hit.has(c.id) ? { ...c, kundprojektIds: med(c.kundprojektIds) }
      : dit.has(c.id) ? { ...c, kundprojektIds: utan(c.kundprojektIds) } : c)));
  }

  async function sparaKontaktLinkedIn(contact, mapped, url) {
    const falt = kontaktFaltFranLinkedIn(contact, mapped, url);
    await updateDoc(doc(db, 'crmCompanies', companyId, 'contacts', contact.id), falt);
    sparaKontaktfoto(companyId, contact.id, { ...contact, ...falt }); // i bakgrunden
    setContacts(prev => prev.map(c => (c.id === contact.id ? { ...c, ...falt } : c)));
  }

  async function handleCompanyEnrich() {
    if (!form.linkedin) return;
    setEnrichingCompany(true);
    setEnrichCompanyMsg('');
    try {
      const data = await enrichCompany(form.linkedin);
      const mapped = mapCompanyToForm(data);
      setForm(f => ({
        ...f,
        name:        mapped.name        || f.name,
        website:     mapped.website     || f.website,
        description: mapped.description || f.description,
        employees:   mapped.employees   || f.employees,
        foundedYear: mapped.foundedYear || f.foundedYear,
        companyType: mapped.companyType || f.companyType,
        city:        mapped.city        || f.city,
        address:     mapped.address     || f.address,
        specialties: mapped.specialties || f.specialties,
        logoUrl:     mapped.logoUrl     || f.logoUrl,
      }));
      setEnrichCompanyMsg(`Berikade: ${mapped.name || 'klart'}`);
    } catch (e) {
      setEnrichCompanyMsg(e.message || 'Kunde inte hämta data');
    } finally {
      setEnrichingCompany(false);
    }
  }

  async function handleContactEnrich() {
    if (!contactForm.linkedin) return;
    setEnrichingContact(true);
    setEnrichContactMsg('');
    try {
      const data = await enrichPerson(contactForm.linkedin);
      const mapped = mapPersonToContact(data);
      setContactForm(f => ({
        ...f,
        name:              mapped.name              || f.name,
        role:              mapped.role              || f.role,
        email:             mapped.email             || f.email,
        phone:             mapped.phone             || f.phone,
        location:          mapped.location          || f.location,
        summary:           mapped.summary           || f.summary,
        photoUrl:          mapped.photoUrl          || f.photoUrl,
        linkedinHeadline:  mapped.linkedinHeadline  ?? f.linkedinHeadline ?? '',
        currentCompany:    mapped.currentCompany    ?? f.currentCompany    ?? '',
        education:         mapped.education         ?? f.education         ?? '',
        languages:         mapped.languages         ?? f.languages         ?? '',
        experience:        mapped.experience        ?? f.experience        ?? '',
        certifications:    mapped.certifications    ?? f.certifications    ?? '',
        skills:            mapped.skills            ?? f.skills            ?? '',
      }));
      setEnrichContactMsg(`Berikade: ${mapped.name || 'klart'}`);
    } catch (e) {
      setEnrichContactMsg(e.message || 'Kunde inte hämta data');
    } finally {
      setEnrichingContact(false);
    }
  }

  // Fyller kontaktens tomma fält från signaturen.
  async function tolkaSignatur(text) {
    const falt = await signatur.tolka(text);
    setContactForm(f => ({
      ...f,
      ...Object.fromEntries(Object.entries(falt).filter(([k]) => !String(f[k] || '').trim())),
    }));
  }

  function openNewContact() {
    setContactForm(EMPTY_CONTACT);
    setEditingContactId(null);
    signatur.aterstall();
    setContactPaste('');
    setParsedCompanyFromPaste(null);
    setEnrichContactMsg('');
    setContactDialog(true);
  }

  function openEditContact(contact) {
    setContactForm({ ...contact });
    setEditingContactId(contact.id);
    signatur.aterstall();
    setContactPaste('');
    setParsedCompanyFromPaste(null);
    setEnrichContactMsg('');
    setContactDialog(true);
  }

  async function handleSaveContact() {
    setSaving(true);
    try {
      const { id, ...raw } = contactForm;
      // Firestore rejects undefined values — strip them out
      const data = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined));
      if (editingContactId) {
        await updateDoc(doc(db, 'crmCompanies', companyId, 'contacts', editingContactId), data);
        sparaKontaktfoto(companyId, editingContactId, data); // i bakgrunden
      } else {
        const ref = await addDoc(collection(db, 'crmCompanies', companyId, 'contacts'), data);
        sparaKontaktfoto(companyId, ref.id, data);
      }
      // Kundens uppgifter ur signaturen: tomma fält och valda avvikande värden.
      const kundAndringar = signatur.kundUppdateringar();
      if (Object.keys(kundAndringar).length) {
        await updateDoc(doc(db, 'crmCompanies', companyId), { ...kundAndringar, updatedAt: new Date() });
        setCompany(c => ({ ...c, ...kundAndringar }));
        setForm(f => ({ ...f, ...kundAndringar }));
      }
      signatur.aterstall();
      setContactDialog(false);
      const snap = await getDocs(query(
        collection(db, 'crmCompanies', companyId, 'contacts'), orderBy('name')
      ));
      setContacts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('handleSaveContact', e);
      alert(`Kunde inte spara kontakt: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteContact(contactId) {
    await deleteDoc(doc(db, 'crmCompanies', companyId, 'contacts', contactId));
    setContacts(prev => prev.filter(c => c.id !== contactId));
  }

  function parseOutlookEmailList(str) {
    if (!str?.trim()) return [];
    const results = [];
    const re = /(?:"([^"]*)"[^<]*<([^>]+)>|<([^>]+)>|([\w.+%-]+@[\w.-]+\.[a-zA-Z]{2,}))/g;
    let m;
    while ((m = re.exec(str)) !== null) {
      const display = (m[1] || '').trim();
      const email = (m[2] || m[3] || m[4] || '').trim().toLowerCase();
      if (!email || results.some(r => r.email === email)) continue;
      let name;
      if (display && !display.includes('@')) {
        name = display;
      } else {
        const local = email.split('@')[0];
        name = local.split(/[.-]/).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
      }
      results.push({ name, email });
    }
    return results;
  }

  async function handleBulkAdd() {
    if (!bulkParsed.length) return;
    setBulkAdding(true);
    try {
      for (const { name, email } of bulkParsed) {
        const data = Object.fromEntries(
          Object.entries({ ...EMPTY_CONTACT, name, email }).filter(([, v]) => v !== undefined)
        );
        await addDoc(collection(db, 'crmCompanies', companyId, 'contacts'), data);
      }
      const snap = await getDocs(query(collection(db, 'crmCompanies', companyId, 'contacts'), orderBy('name')));
      setContacts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setBulkDialog(false);
      setBulkText('');
      setBulkParsed([]);
    } finally {
      setBulkAdding(false);
    }
  }

  async function openEmpDialog() {
    setEmpDialog(true);
    setEmpList([]);
    setEmpNextPage(null);
    setEmpSelected(new Set());
    setEmpError('');
    setEmpSearch(empPreSearch.trim());
    setEmpSort('senior');
    await fetchEmployees(null, true);
  }

  function normalizeLinkedinUrl(url) {
    if (!url) return '';
    let u = url.trim().replace(/\/$/, '');
    if (!u.startsWith('http')) u = 'https://' + u;
    // Ensure www. prefix
    u = u.replace(/^https?:\/\/((?!www\.))/, 'https://www.');
    return u;
  }

  async function fetchEmployees(page = null, reset = false) {
    setEmpLoading(true);
    setEmpError('');
    try {
      const linkedinUrl = normalizeLinkedinUrl(company.linkedin);
      const data = await enrichCompanyEmployees(linkedinUrl, page);
      const results = data.results || data.employees || [];
      setEmpList(prev => reset ? results : [...prev, ...results]);
      setEmpNextPage(data.next_page || null);
    } catch (e) {
      setEmpError(e.message || 'Kunde inte hämta medarbetare');
    } finally {
      setEmpLoading(false);
    }
  }

  async function handleAddEmployees() {
    const toAdd = empList.filter(e => empSelected.has(e.profile_url));
    if (!toAdd.length) return;
    setEmpAdding(true);
    const existingLinkedins = new Set(contacts.map(c => c.linkedin).filter(Boolean));
    for (const emp of toAdd) {
      if (existingLinkedins.has(emp.profile_url)) continue; // already added
      const mapped = mapPersonToContact(emp.profile);
      const contactData = Object.fromEntries(
        Object.entries({ ...EMPTY_CONTACT, ...mapped, linkedin: emp.profile_url, primary: false })
          .filter(([, v]) => v !== undefined)
      );
      const ref = await addDoc(collection(db, 'crmCompanies', companyId, 'contacts'), contactData);
      sparaKontaktfoto(companyId, ref.id, contactData); // i bakgrunden
    }
    const snap = await getDocs(query(
      collection(db, 'crmCompanies', companyId, 'contacts'), orderBy('name')
    ));
    setContacts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setEmpDialog(false);
    setEmpSelected(new Set());
    setEmpAdding(false);
  }

  function toggleEmpSelect(profileUrl) {
    setEmpSelected(prev => {
      const next = new Set(prev);
      next.has(profileUrl) ? next.delete(profileUrl) : next.add(profileUrl);
      return next;
    });
  }

  async function handleLoadFinancials() {
    const orgNr = normalizeOrgNr(company?.orgNumber);
    if (!orgNr) return;
    setLoadingFinancials(true);
    try {
      const res = await authFetch(`/.netlify/functions/bolagsverket-financials?orgnr=${encodeURIComponent(orgNr)}`);
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch (_) {
        alert(`Servern svarade HTTP ${res.status} (ej JSON). Kontrollera Netlify-loggar.`);
        return;
      }
      if (!res.ok) {
        alert((data.error || `Fel ${res.status}`) + (data.detail ? '\n' + data.detail : ''));
        return;
      }
      setFinancials(data.financials || []);
    } catch (e) {
      alert('Nätverksfel: ' + e.message);
    } finally {
      setLoadingFinancials(false);
    }
  }

  async function handleBolagsverketLookup() {
    const raw = form.orgNumber || company?.orgNumber;
    if (!raw) return;
    const problem = orgNrProblem(raw);
    if (problem) { alert(problem); return; }
    const orgNr = normalizeOrgNr(raw);
    setLookingUp(true);
    try {
      const res = await authFetch(`/.netlify/functions/bolagsverket?orgnr=${encodeURIComponent(orgNr)}`);
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch (_) {
        alert(`Servern svarade HTTP ${res.status} (ej JSON):\n${text.slice(0, 200)}`);
        return;
      }
      if (!res.ok) {
        const msg = data.error || `Fel ${res.status}`;
        const detail = res.status === 403
          ? 'Åtkomst nekad (403). Kontrollera att "Företagsinformation"-API:et är aktiverat i din Bolagsverket-prenumeration på developer portal.'
          : (data.detail || '');
        alert(msg + (detail ? '\n\n' + detail : ''));
        return;
      }
      const prevName = form.name;
      setForm(f => ({
        ...f,
        // Namn hämtas från BolagsAPI (officiellt registrerat namn)
        name:           data.name || f.name,
        description:    data.description    || f.description,
        // Postadress (registrerad adress)
        address:        data.address?.street || f.address,
        zip:            data.address?.zip    || f.zip,
        city:           data.address?.city   || f.city,
        // Besöksadress med eget postnr/ort
        visitStreet:    data.visitingAddress?.street || f.visitStreet,
        visitZip:       data.visitingAddress?.zip    || f.visitZip,
        visitCity:      data.visitingAddress?.city   || f.visitCity,
        industry:       f.industry || (data.sniCodes?.[0]?.label ?? f.industry),
        website:        data.website        || f.website,
        linkedin:       data.linkedin       || f.linkedin,
        companyForm:    data.companyForm    || f.companyForm,
        fSkatt:         data.fSkatt         ?? f.fSkatt,
        stabilityGrade: data.stabilityGrade || f.stabilityGrade,
        registeredDate: data.registeredDate || f.registeredDate,
        // SNI-koder sparas som JSON-sträng för Firebase
        sniCodes:       data.sniCodes?.length ? JSON.stringify(data.sniCodes) : f.sniCodes,
      }));
      // Säkerställ att sniCodes alltid är array, flagga namnändring
      setLookupResult({
        ...data,
        sniCodes: Array.isArray(data.sniCodes) ? data.sniCodes : [],
        nameChanged: data.name && prevName && data.name !== prevName ? prevName : null,
      });
    } catch (e) {
      alert('Kunde inte nå Bolagsverket: ' + e.message);
    } finally {
      setLookingUp(false);
    }
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (!company) return (
    <Box sx={{ p: 4 }}>
      <Typography>Företaget hittades inte.</Typography>
      <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>Tillbaka</Button>
    </Box>
  );

  const companyDepts = departments.filter(d => (company.departmentIds || []).includes(d.id));
  const statusInfo = companyStatusInfo(company.status);
  const mapAddress = [company.address, company.city].filter(Boolean).join(', ');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Toolbar */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small"
            onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">{company.name}</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {[company.city, company.industry].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<AddIcon />}
            onClick={() => navigate(`/crm/leads?new=1&companyId=${companyId}`)}
            sx={{ color: '#FF6B00', borderColor: '#FF6B00', bgcolor: 'rgba(255,107,0,0.08)',
              '&:hover': { bgcolor: 'rgba(255,107,0,0.18)', borderColor: '#FF6B00' } }}>
            Skapa lead
          </Button>
          <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />}
            onClick={() => setCopyCustomerOpen(true)}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': { borderColor: 'white' } }}>
            Kopiera kunddata
          </Button>
          <Button size="small" variant="outlined" startIcon={<EditIcon />}
            onClick={() => { setForm(company); setEditing(true); }}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': { borderColor: 'white' } }}>
            Redigera
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Säkerhetsflaggorna överst på kortet */}
        <KundSakerhetFlaggor kund={company}
          onSparad={falt => { setCompany(c => ({ ...c, ...falt })); setForm(f => ({ ...f, ...falt })); }} />
        <Grid container spacing={3} alignItems="flex-start">

          {/* LEFT – grundinfo + karta */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, mb: 2 }}>
              {/* Header row */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                <Avatar src={company.logoUrl || undefined}
                  sx={{ bgcolor: '#1a1a1a', width: 56, height: 56, fontSize: '1rem', flexShrink: 0 }}>
                  {!company.logoUrl && initials(company.name)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  {editing ? (
                    <TextField size="small" fullWidth value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      sx={{ mb: 1 }} label="Företagsnamn" />
                  ) : (
                    <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                      {company.name}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {statusInfo && (
                      <Chip label={statusInfo.label} size="small"
                        sx={{ fontSize: '0.65rem', height: 20,
                          bgcolor: statusInfo.bg, color: statusInfo.color, fontWeight: 600 }} />
                    )}
                    {company.isReseller && (
                      <Chip label="Återförsäljare" size="small"
                        sx={{ fontSize: '0.65rem', height: 20,
                          bgcolor: '#7B1FA2', color: 'white', fontWeight: 600 }} />
                    )}
                    {company.isSupplier && (
                      <Chip label="Leverantör" size="small"
                        sx={{ fontSize: '0.65rem', height: 20,
                          bgcolor: '#1B5E20', color: 'white', fontWeight: 600 }} />
                    )}
                    {company.sakerhetsskydd && (
                      <Chip label="Säkerhetsskyddslagen" size="small"
                        sx={{ fontSize: '0.65rem', height: 20,
                          bgcolor: '#B71C1C', color: 'white', fontWeight: 600 }} />
                    )}
                    {!company.orgNumber && (
                      <Chip label="Saknar org.nr" size="small" variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20, color: '#FFB74D', borderColor: '#FFB74D' }} />
                    )}
                    {companyDepts.map(d => (
                      <Tooltip key={d.id} title={d.name}>
                        <Chip label={deptCode(d)} size="small"
                          sx={{ fontSize: '0.62rem', height: 20,
                            bgcolor: '#1a1a1a', color: 'white' }} />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Read-only company info */}
              <Box>
                {/* BolagsAPI-chips */}
                {(company.companyForm || company.fSkatt != null || company.stabilityGrade || company.registeredDate) && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {company.companyForm && (
                      <Chip size="small" label={company.companyForm} sx={{ bgcolor: '#e3f2fd', color: '#1565C0', fontSize: '0.7rem' }} />
                    )}
                    {company.fSkatt === true && (
                      <Chip size="small" label="F-skatt" sx={{ bgcolor: '#e8f5e9', color: '#2E7D32', fontSize: '0.7rem' }} />
                    )}
                    {company.fSkatt === false && (
                      <Chip size="small" label="Ej F-skatt" sx={{ bgcolor: '#fce4ec', color: '#c62828', fontSize: '0.7rem' }} />
                    )}
                    {company.stabilityGrade && (
                      <Chip size="small" label={`Stabilitet: ${company.stabilityGrade}`}
                        sx={{ bgcolor: '#fff8e1', color: '#e65100', fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                    {company.registeredDate && (
                      <Chip size="small" label={`Reg. ${company.registeredDate}`}
                        sx={{ bgcolor: '#f5f5f5', color: '#555', fontSize: '0.7rem' }} />
                    )}
                  </Box>
                )}

                {/* SNI-koder från Firebase */}
                {(() => {
                  try {
                    const codes = company.sniCodes ? JSON.parse(company.sniCodes) : [];
                    if (!Array.isArray(codes) || codes.length === 0) return null;
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {codes.map((s, i) => (
                          <Chip key={i} size="small"
                            label={s.code ? `${s.code}` : s.label}
                            title={s.label}
                            sx={{ fontSize: '0.65rem', bgcolor: '#f0f4ff', color: '#333' }} />
                        ))}
                      </Box>
                    );
                  } catch { return null; }
                })()}

                {company.orgNumber && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <FieldRow label="Org.nr" value={formatOrgNumber(company.orgNumber)} />
                    </Box>
                    <Tooltip title="Slå upp på allabolag.se">
                      <IconButton size="small" onClick={() =>
                        window.open(`https://www.allabolag.se/${company.orgNumber.replace(/\D/g, '')}`, '_blank')}
                        sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                <FieldRow label="Bransch" value={company.industry} />
                {company.segment && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                    <Typography variant="caption" color="text.disabled" sx={{ width: 110, flexShrink: 0 }}>Segment</Typography>
                    <SegmentEtikett segment={company.segment} />
                  </Box>
                )}
                <FieldRow label="Organisationstyp" value={company.organisationstyp} />
                <FieldRow label="Omsättning" value={company.revenue} />
                <FieldRow label="Anställda" value={company.employees} />
                {company.visitStreet && (
                  <FieldRow label="Besöksadress"
                    value={[company.visitStreet, company.visitZip, company.visitCity].filter(Boolean).join(', ')} />
                )}
                <FieldRow label="Postadress"
                  value={[company.address, company.zip, company.city].filter(Boolean).join(', ')} />
                <FieldRow label="Land" value={company.country} />
                {(company.invoiceStreet || company.invoiceEmail || company.invoiceGLN || company.invoicePeppol) && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    {(company.invoiceStreet || company.invoiceZip || company.invoiceCity) && (
                      <FieldRow label="Fakturaadress"
                        value={[company.invoiceStreet, company.invoiceZip, company.invoiceCity].filter(Boolean).join(', ')} />
                    )}
                    <FieldRow label="Faktura e-post" value={company.invoiceEmail} kopiera
                      href={company.invoiceEmail ? `mailto:${company.invoiceEmail}` : null} />
                    <FieldRow label="GLN / EAN"    value={company.invoiceGLN} />
                    <FieldRow label="Peppol-ID"    value={company.invoicePeppol} />
                    <Divider sx={{ my: 1 }} />
                  </>
                )}
                <FieldRow label="Telefon" value={company.phone} kopiera />
                <FieldRow label="Webbplats" value={company.website}
                  href={company.website?.startsWith('http') ? company.website : company.website ? 'https://' + company.website : null} />
                <FieldRow label="LinkedIn" value={company.linkedin}
                  href={company.linkedin?.startsWith('http') ? company.linkedin : company.linkedin ? 'https://' + company.linkedin : null} />
                {!company.linkedin && !editing && (
                  <LinkedInHamtning typ="company" namn={company.name}
                    onHamtad={sparaKundLinkedIn} />
                )}
                {company.description && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                      Verksamhet
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{
                      display: '-webkit-box', WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: descExpanded ? 'none' : 4,
                      overflow: 'hidden', whiteSpace: 'pre-wrap',
                    }}>
                      {company.description}
                    </Typography>
                    {company.description.length > 200 && (
                      <Typography variant="caption" onClick={() => setDescExpanded(e => !e)}
                        sx={{ color: '#FF6B00', cursor: 'pointer', display: 'block', mt: 0.5 }}>
                        {descExpanded ? 'Dölj' : 'Läs mer'}
                      </Typography>
                    )}
                  </>
                )}
                {company.notes && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="body2" color="text.secondary" sx={{
                      display: '-webkit-box', WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: notesExpanded ? 'none' : 4,
                      overflow: 'hidden', whiteSpace: 'pre-wrap',
                    }}>
                      {company.notes}
                    </Typography>
                    {company.notes.length > 200 && (
                      <Typography variant="caption" onClick={() => setNotesExpanded(e => !e)}
                        sx={{ color: '#FF6B00', cursor: 'pointer', display: 'block', mt: 0.5 }}>
                        {notesExpanded ? 'Dölj' : 'Läs mer'}
                      </Typography>
                    )}
                  </>
                )}

                {/* Finansiell historik */}
                {company.orgNumber && (
                  <Box sx={{ mt: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Finansiell historik
                      </Typography>
                      <Tooltip title={financials === null ? 'Hämta finansiell historik via BolagsAPI' : 'Uppdatera finansiell historik'}>
                        <span>
                          <IconButton size="small" onClick={handleLoadFinancials} disabled={loadingFinancials}
                            sx={{ borderRadius: 1, p: '3px' }}>
                            {loadingFinancials
                              ? <CircularProgress size={14} />
                              : <Box component="img" src="/bolagsapi_logo.svg" alt="BolagsAPI"
                                  sx={{ height: 16, width: 'auto' }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    {financials !== null && (
                      financials.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Inga årsredovisningar hittades
                        </Typography>
                      ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #eee' }}>
                                <th style={{ textAlign: 'left', padding: '4px 6px', color: '#888', fontWeight: 600 }}>År</th>
                                <th style={{ textAlign: 'right', padding: '4px 6px', color: '#888', fontWeight: 600 }}>Omsättning</th>
                                <th style={{ textAlign: 'right', padding: '4px 6px', color: '#888', fontWeight: 600 }}>Resultat</th>
                                <th style={{ textAlign: 'right', padding: '4px 6px', color: '#888', fontWeight: 600 }}>Eget kapital</th>
                              </tr>
                            </thead>
                            <tbody>
                              {financials.map((row, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                  <td style={{ padding: '4px 6px', fontWeight: 600 }}>{row.year ?? '–'}</td>
                                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                                    {row.nettoomsattning != null
                                      ? (row.nettoomsattning / 1_000_000).toLocaleString('sv-SE', { maximumFractionDigits: 1 }) + ' MSEK'
                                      : '–'}
                                  </td>
                                  <td style={{
                                    padding: '4px 6px', textAlign: 'right',
                                    color: row.resultat != null ? (row.resultat >= 0 ? '#2E7D32' : '#C62828') : 'inherit',
                                    fontWeight: 500
                                  }}>
                                    {row.resultat != null
                                      ? (row.resultat / 1_000_000).toLocaleString('sv-SE', { maximumFractionDigits: 1 }) + ' MSEK'
                                      : '–'}
                                  </td>
                                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                                    {row.egetKapital != null
                                      ? (row.egetKapital / 1_000_000).toLocaleString('sv-SE', { maximumFractionDigits: 1 }) + ' MSEK'
                                      : '–'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      )
                    )}
                  </Box>
                )}
              </Box>
            </Paper>

            {/* ── ÅTERFÖRSÄLJARE-sektion ── */}
            {company.isReseller && (
              <Paper sx={{ p: 3, mb: 2, borderLeft: '4px solid #7B1FA2' }}>
                <Typography variant="caption" fontWeight="bold" color="#7B1FA2"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                  Återförsäljare
                </Typography>
                <FieldRow label="Avtalsdatum"     value={company.resellerAgreementDate} />
                <FieldRow label="Täckningsområde" value={company.resellerRegion} />
                <FieldRow label="Slutkunder (ca)" value={company.resellerEndCustomers} />
                {(company.resellerProducts || []).length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                      Produkter &amp; avtal
                    </Typography>
                    {(company.resellerProducts || []).map((rp, i) => {
                      const prod = allProducts.find(p => p.id === rp.productId);
                      return (
                        <Box key={i} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap',
                          mb: 0.75, p: 1, bgcolor: '#f9f0ff', borderRadius: 1 }}>
                          <Chip size="small" label={prod?.name || rp.productId}
                            sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 600 }} />
                          {rp.level && <Chip size="small" label={rp.level} variant="outlined"
                            sx={{ borderColor: '#7B1FA2', color: '#7B1FA2' }} />}
                          {rp.discount && <Chip size="small" label={`Rabatt: ${rp.discount}`} />}
                          {rp.notes && (
                            <Typography variant="caption" color="text.secondary"
                              sx={{ alignSelf: 'center' }}>{rp.notes}</Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            )}

            {/* ── LEVERANTÖR-sektion ── */}
            {company.isSupplier && (
              <Paper sx={{ p: 3, mb: 2, borderLeft: '4px solid #1B5E20' }}>
                <Typography variant="caption" fontWeight="bold" color="#1B5E20"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                  Leverantör
                </Typography>
                <FieldRow label="Avtalsstatus"    value={company.supplierContractStatus} />
                <FieldRow label="Avtalstyp"       value={company.supplierContractType} />
                <FieldRow label="Avtalsnummer"    value={company.supplierContractNumber} />
                <FieldRow label="Avtalsperiod"
                  value={[company.supplierContractStart, company.supplierContractEnd].filter(Boolean).join(' – ')} />
                <FieldRow label="Uppsägningstid"  value={company.supplierNoticePeriod} />
                <FieldRow label="Kontraktsvärde"  value={company.supplierContractValue} />
                <FieldRow label="Betalnvillkor"   value={company.supplierPaymentTerms} />
                {company.supplierDelivery && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                      Vad de levererar
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {company.supplierDelivery}
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}

            {/* ── KOPPLADE AVTAL ── */}
            {linkedContracts.length > 0 && (
              <Paper sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <GavelIcon sx={{ fontSize: 16, color: '#FF6B00' }} />
                  <Typography variant="caption" fontWeight="bold" color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Kopplade avtal
                  </Typography>
                </Box>
                {linkedContracts.map(c => {
                  const days = c.endDate
                    ? Math.ceil((new Date(c.endDate) - new Date()) / 86400000)
                    : null;
                  const deadlineColor = days === null ? null
                    : days < 0    ? '#C62828'
                    : days <= 60  ? '#E65100'
                    : '#2E7D32';
                  return (
                    <Box key={c.id} sx={{ display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', py: 1,
                      borderBottom: '1px solid', borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none', pb: 0 } }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{c.title}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                          {c.contractType && <Chip size="small" label={c.contractType} sx={{ fontSize: '0.62rem', height: 18 }} />}
                          {c.category    && <Chip size="small" label={c.category}    sx={{ fontSize: '0.62rem', height: 18 }} />}
                          {c.value       && <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>{c.value}</Typography>}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1 }}>
                        {c.endDate && (
                          <Typography variant="caption" sx={{ color: deadlineColor, fontWeight: 600 }}>
                            {days < 0 ? `Utgick ${c.endDate}` : `t.o.m. ${c.endDate}`}
                          </Typography>
                        )}
                        {c.fileUrl && (
                          <Tooltip title="Öppna avtalsdokument">
                            <IconButton size="small" onClick={() => window.open(c.fileUrl, '_blank')}>
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            )}

            {/* Map */}
            {mapAddress && (
              <Paper sx={{ p: 0, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <LocationOnIcon sx={{ fontSize: 16, color: '#FF6B00' }} />
                    <Typography variant="caption" fontWeight="bold" color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Karta
                    </Typography>
                  </Box>
                  <Tooltip title="Öppna i Google Maps">
                    <IconButton size="small"
                      onClick={() => window.open(
                        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapAddress)}`,
                        '_blank'
                      )}>
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                {mapCoords ? (
                  <iframe
                    title="Karta"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapCoords.lon - 0.012},${mapCoords.lat - 0.008},${mapCoords.lon + 0.012},${mapCoords.lat + 0.008}&layer=mapnik&marker=${mapCoords.lat},${mapCoords.lon}`}
                    width="100%"
                    height="220"
                    style={{ border: 0, display: 'block' }}
                  />
                ) : (
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                      Geocodning pågår... Kontrollera att adress och ort är ifyllda.
                    </Alert>
                  </Box>
                )}
              </Paper>
            )}
          </Grid>

          {/* RIGHT – kundprojekt, kontakter + references */}
          <Grid item xs={12} md={7}>
            {/* Kundprojekt – kundens projekt som säljunderlag (inte Tikabs uppdrag) */}
            <Paper sx={{ p: 2.5, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ConstructionIcon sx={{ fontSize: 18, color: '#FF6B00' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>Kundprojekt</Typography>
                <KundprojektKnapp kund={{ id: companyId, name: company.name }} uid={currentUser?.uid} onSparad={kundprojektSparat} />
              </Box>
              <KundprojektLista kps={kundprojekt} kundId={companyId} leads={kpLeads}
                navigate={navigate} laddar={kpLaddar} uid={currentUser?.uid} onAndrat={kundprojektSparat} />
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ fontSize: 18, color: '#FF6B00' }} />
                  <Typography variant="subtitle2" fontWeight="bold">Kontakter</Typography>
                  {contacts.length > 0 && (
                    <Chip label={contacts.length} size="small"
                      sx={{ height: 18, fontSize: '0.65rem',
                        bgcolor: '#fff3e0', color: '#FF6B00' }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                  {company.linkedin && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField
                        size="small"
                        placeholder="Namn att söka…"
                        value={empPreSearch}
                        onChange={e => setEmpPreSearch(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') openEmpDialog(); }}
                        sx={{ width: 150, '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 } }}
                      />
                      <Button size="small" variant="outlined" startIcon={<LinkedInIcon />}
                        onClick={openEmpDialog}
                        sx={{ fontSize: '0.72rem', borderColor: '#0A66C2', color: '#0A66C2',
                          '&:hover': { borderColor: '#084d95', bgcolor: '#e8f0fe' } }}>
                        Hämta medarbetare
                      </Button>
                    </Box>
                  )}
                  <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={openNewContact}
                    sx={{ fontSize: '0.72rem', borderColor: '#FF6B00', color: '#FF6B00',
                      '&:hover': { borderColor: '#e55a00', bgcolor: '#fff3ec' } }}>
                    Ny kontakt
                  </Button>
                  <Button size="small" variant="outlined"
                    onClick={() => { setBulkText(''); setBulkParsed([]); setBulkDialog(true); }}
                    sx={{ fontSize: '0.72rem', borderColor: '#555', color: '#555',
                      '&:hover': { borderColor: '#222', bgcolor: '#f5f5f5' } }}>
                    Klistra in e-postlista
                  </Button>
                </Box>
              </Box>

              {contacts.length === 0 ? (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <PersonIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Inga kontakter tillagda ännu
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {contacts.map(contact => (
                    <Box key={contact.id} sx={{
                      p: 2, borderRadius: 2,
                      border: '1px solid', borderColor: 'divider',
                      display: 'flex', alignItems: 'flex-start', gap: 2,
                      '&:hover .contact-actions': { opacity: 1 },
                      '&:hover': { borderColor: '#FF6B00', bgcolor: '#fffaf6' },
                      transition: 'all 0.15s'
                    }}>
                      <Avatar src={contact.photoUrl || undefined}
                        sx={{ bgcolor: '#FF6B00', width: 40, height: 40,
                          fontSize: '0.8rem', flexShrink: 0 }}>
                        {!contact.photoUrl && initials(contact.name)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {contact.name}
                          </Typography>
                          {contact.primary && (
                            <Chip label="Primär" size="small"
                              sx={{ fontSize: '0.6rem', height: 18,
                                bgcolor: '#e3f2fd', color: '#1565C0' }} />
                          )}
                        </Box>
                        {(contact.role || contact.department) && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {[contact.role, contact.department].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                        {contact.dealRelation && (
                          <Typography variant="caption" sx={{ color: '#FF6B00' }} display="block">
                            {contact.dealRelation}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75, flexWrap: 'wrap' }}>
                          {contact.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <EmailIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                              <Typography variant="caption" component="a"
                                href={`mailto:${contact.email}`}
                                sx={{ color: 'text.secondary', textDecoration: 'none',
                                  '&:hover': { color: '#FF6B00' } }}>
                                {contact.email}
                              </Typography>
                              <KopieraKnapp text={contact.email} size={11} />
                            </Box>
                          )}
                          {contact.mobile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <SmartphoneIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                              <Typography variant="caption" color="text.secondary">
                                {contact.mobile}
                              </Typography>
                              <KopieraKnapp text={contact.mobile} size={11} />
                            </Box>
                          )}
                          {contact.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <PhoneIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                              <Typography variant="caption" color="text.secondary">
                                {contact.phone}
                              </Typography>
                              <KopieraKnapp text={contact.phone} size={11} />
                            </Box>
                          )}
                          {contact.linkedin && (
                            <Tooltip title={contact.linkedin}>
                              <IconButton size="small" sx={{ p: 0 }}
                                onClick={() => window.open(contact.linkedin, '_blank')}>
                                <LinkedInIcon sx={{ fontSize: 14, color: '#0A66C2' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {!contact.linkedin && (
                          <LinkedInHamtning typ="person" kompakt namn={contact.name} foretag={company.name}
                            onHamtad={(mapped, url) => sparaKontaktLinkedIn(contact, mapped, url)} />
                        )}
                        {contact.notes && (
                          <Box sx={{ mt: 0.75, p: 1, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary"
                              sx={{ fontStyle: 'italic' }}>
                              {contact.notes}
                            </Typography>
                          </Box>
                        )}
                        <KontaktKundprojekt kps={kundprojekt} kundId={companyId} kontaktId={contact.id} navigate={navigate} />
                        <KundprojektKnapp liten sx={{ mt: 0.5, ml: -0.75 }}
                          kund={{ id: companyId, name: company.name }}
                          kontakt={{ id: contact.id, name: contact.name }}
                          uid={currentUser?.uid} onSparad={kundprojektSparat} />
                      </Box>
                      <Box className="contact-actions"
                        sx={{ opacity: 0, transition: 'opacity 0.15s',
                          display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => openEditContact(contact)}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                        <IconButton size="small"
                          onClick={() => handleDeleteContact(contact.id)}
                          sx={{ '&:hover': { color: 'error.main' } }}>
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>

            {/* Referensuppdrag */}
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', mb: 2.5 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Referensuppdrag
                </Typography>
                <Button size="small" startIcon={<AddIcon />}
                  onClick={openNewRef} variant="outlined"
                  sx={{ borderColor: '#FF6B00', color: '#FF6B00' }}>
                  Lägg till
                </Button>
              </Box>
              {references.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Inga referensuppdrag registrerade ännu
                </Typography>
              ) : (
                references.map(ref => (
                  <Box key={ref.id} sx={{ mb: 2, p: 2, borderRadius: 2,
                    border: '1px solid', borderColor: 'divider',
                    '&:hover .ref-actions': { opacity: 1 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{ref.name}</Typography>
                        {ref.role && (
                          <Typography variant="caption" color="text.secondary">
                            {ref.role}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          {ref.startDate && (
                            <Typography variant="caption" color="text.secondary">
                              {ref.startDate}{ref.endDate ? ' – ' + ref.endDate : ''}
                            </Typography>
                          )}
                          {ref.budget && (
                            <Typography variant="caption" color="text.secondary">
                              · {ref.budget}
                            </Typography>
                          )}
                        </Box>
                        {ref.keywords?.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {ref.keywords.map((k, i) => (
                              <Chip key={i} label={k} size="small"
                                sx={{ height: 18, fontSize: '0.6rem' }} />
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Box className="ref-actions"
                        sx={{ opacity: 0, transition: 'opacity 0.15s', display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => openEditRef(ref)}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteRef(ref.id)}
                          sx={{ '&:hover': { color: 'error.main' } }}>
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Redigera företag – dialog */}
      <Dialog open={editing} onClose={() => { setEditing(false); setForm(company); }}
        maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EditIcon sx={{ color: '#FF6B00', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">Redigera företag</Typography>
              <Typography variant="caption" color="text.secondary">{company?.name}</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, px: 3 }}>
          <Grid container spacing={2}>

            {/* ── IDENTITET ── */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 1.5 }}>
                  <TextField label="Företagsnamn *" autoFocus
                    value={form.name || ''}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    sx={{ flex: '1 1 260px', minWidth: 200 }} />
                  <TextField select label="Kundstatus"
                    value={form.status || ''}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: '0 1 160px', minWidth: 140 }}>
                    <MenuItem value=""><Typography color="text.disabled" variant="body2">Ange status</Typography></MenuItem>
                    {COMPANY_STATUSES.map(s => (
                      <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Org.nr" placeholder="Ange org.nr"
                    value={form.orgNumber || ''}
                    onChange={e => setForm(f => ({ ...f, orgNumber: e.target.value }))}
                    sx={{ flex: '0 1 200px', minWidth: 160 }}
                    InputProps={{
                      endAdornment: form.orgNumber && (
                        <Tooltip title="Hämta företagsinfo via BolagsAPI">
                          <IconButton size="small" onClick={handleBolagsverketLookup} disabled={lookingUp}
                            sx={{ borderRadius: 1, p: '3px' }}>
                            {lookingUp
                              ? <CircularProgress size={14} />
                              : <Box component="img" src="/bolagsapi_logo.svg" alt="BolagsAPI"
                                  sx={{ height: 16, width: 'auto' }} />}
                          </IconButton>
                        </Tooltip>
                      )
                    }} />
                  <Chip label="Återförsäljare" size="small" clickable
                    onClick={() => setForm(f => ({ ...f, isReseller: !f.isReseller }))}
                    variant={form.isReseller ? 'filled' : 'outlined'}
                    sx={{
                      alignSelf: 'center', mt: 1,
                      bgcolor: form.isReseller ? '#7B1FA2' : 'transparent',
                      color: form.isReseller ? 'white' : '#7B1FA2',
                      borderColor: '#7B1FA2', fontWeight: 600, fontSize: '0.72rem',
                    }} />
                  <Chip label="Leverantör" size="small" clickable
                    onClick={() => setForm(f => ({ ...f, isSupplier: !f.isSupplier }))}
                    variant={form.isSupplier ? 'filled' : 'outlined'}
                    sx={{
                      alignSelf: 'center', mt: 1,
                      bgcolor: form.isSupplier ? '#1B5E20' : 'transparent',
                      color: form.isSupplier ? 'white' : '#2E7D32',
                      borderColor: '#2E7D32', fontWeight: 600, fontSize: '0.72rem',
                    }} />
                  <Chip label="Säkerhetsskyddslagen" size="small" clickable
                    onClick={() => setForm(f => ({ ...f, sakerhetsskydd: !f.sakerhetsskydd }))}
                    variant={form.sakerhetsskydd ? 'filled' : 'outlined'}
                    sx={{
                      alignSelf: 'center', mt: 1,
                      bgcolor: form.sakerhetsskydd ? '#B71C1C' : 'transparent',
                      color: form.sakerhetsskydd ? 'white' : '#B71C1C',
                      borderColor: '#B71C1C', fontWeight: 600, fontSize: '0.72rem',
                    }} />
                </Box>

                {/* LinkedIn – hämta-rad */}
                <Box sx={{ bgcolor: 'rgba(0,119,181,0.05)', borderRadius: 1.5, px: 1.5, py: 1, mb: departments.length ? 1.5 : 0,
                  border: '1px solid rgba(0,119,181,0.15)' }}>
                  <Typography variant="caption" sx={{ color: '#0077b5', display: 'block', mb: 1 }}>
                    ✦ Klistra in LinkedIn-adressen (linkedin.com/company/…) och klicka på ✨ för att automatiskt hämta
                    namn, beskrivning, bransch, logotyp och mer.
                  </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <TextField fullWidth size="small" label="LinkedIn-sida"
                    placeholder="https://www.linkedin.com/company/..."
                    value={form.linkedin || ''}
                    onChange={e => { setForm(f => ({ ...f, linkedin: e.target.value })); setEnrichCompanyMsg(''); }}
                    InputProps={{
                      endAdornment: form.linkedin && (
                        <Tooltip title="Hämta företagsinfo från LinkedIn">
                          <IconButton size="small" onClick={handleCompanyEnrich} disabled={enrichingCompany}
                            sx={{ borderRadius: 1, p: '3px', color: '#0077b5', '&:hover': { bgcolor: 'rgba(0,119,181,0.1)' } }}>
                            {enrichingCompany ? <CircularProgress size={14} /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </Tooltip>
                      )
                    }} />
                  {enrichCompanyMsg && (
                    <Alert severity={enrichCompanyMsg.startsWith('Berikade') ? 'success' : 'error'}
                      sx={{ py: 0, flex: '1 1 200px' }} onClose={() => setEnrichCompanyMsg('')}>
                      <Typography variant="caption">{enrichCompanyMsg}</Typography>
                    </Alert>
                  )}
                </Box>
                </Box>

                {/* Det äldre fältet orgType döljs (datan finns kvar och används som ledtråd
                    till AI-förslaget) – organisationstyp väljs under Affärsinformation. */}
                {departments.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75, mt: 2 }}>
                      Kopplade affärsområden
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {departments.map(d => {
                        const sel = (form.departmentIds || []).includes(d.id);
                        return (
                          <Tooltip key={d.id} title={d.name}>
                            <Chip label={deptCode(d)} size="small"
                              onClick={() => setForm(f => ({
                                ...f,
                                departmentIds: sel
                                  ? f.departmentIds.filter(id => id !== d.id)
                                  : [...(f.departmentIds || []), d.id]
                              }))}
                              variant={sel ? 'filled' : 'outlined'}
                              sx={{ bgcolor: sel ? '#1a1a1a' : 'transparent',
                                color: sel ? 'white' : 'text.primary', cursor: 'pointer' }} />
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </>
                )}
              </Box>
            </Grid>

            {/* BolagsAPI-resultat */}
            {lookupResult && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ py: 0.5 }}
                  onClose={() => setLookupResult(null)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="caption" fontWeight="bold">Hämtat från</Typography>
                    <Box component="img" src="/bolagsapi_logo.svg" alt="BolagsAPI" sx={{ height: 14, width: 'auto' }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                    {lookupResult.companyForm && (
                      <Chip size="small" label={lookupResult.companyForm} sx={{ bgcolor: '#e3f2fd', color: '#1565C0' }} />
                    )}
                    {lookupResult.status && (
                      <Chip size="small" label={lookupResult.status}
                        sx={{ bgcolor: lookupResult.status === 'Aktiv' ? '#e8f5e9' : '#fce4ec',
                              color: lookupResult.status === 'Aktiv' ? '#2E7D32' : '#c62828' }} />
                    )}
                    {lookupResult.registeredDate && <Chip size="small" label={`Reg. ${lookupResult.registeredDate}`} />}
                    {lookupResult.fSkatt === true && <Chip size="small" label="F-skatt" sx={{ bgcolor: '#e8f5e9', color: '#2E7D32' }} />}
                    {lookupResult.fSkatt === false && <Chip size="small" label="Ej F-skatt" sx={{ bgcolor: '#fce4ec', color: '#c62828' }} />}
                    {lookupResult.stabilityGrade && (
                      <Chip size="small" label={`Stabilitet: ${lookupResult.stabilityGrade}`}
                        sx={{ bgcolor: '#fff8e1', color: '#e65100', fontWeight: 600 }} />
                    )}
                  </Box>
                  {lookupResult.sniCodes?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: lookupResult.nameChanged ? 0.75 : 0 }}>
                      {lookupResult.sniCodes.map((s, i) => (
                        <Chip key={i} size="small" label={s.code ? `${s.code} – ${s.label}` : s.label} />
                      ))}
                    </Box>
                  )}
                  {lookupResult.nameChanged && (
                    <Box sx={{ mt: 0.75, p: 1, bgcolor: '#fff8e1', borderRadius: 1, border: '1px solid #ffe082' }}>
                      <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600 }}>
                        OBS: Företagsnamnet ändrades från &ldquo;{lookupResult.nameChanged}&rdquo; till &ldquo;{lookupResult.name}&rdquo;.
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#555', display: 'block' }}>
                        Du kan justera namnfältet ovan innan du sparar.
                      </Typography>
                    </Box>
                  )}
                </Alert>
              </Grid>
            )}

            {/* ── ÅTERFÖRSÄLJARE (visas bara när taggad) ── */}
            {form.isReseller && (
              <Grid item xs={12}>
                <Box sx={{ bgcolor: '#f9f0ff', borderRadius: 2, p: 2, border: '1px solid #ce93d8' }}>
                  <Typography variant="caption" fontWeight="bold" color="#7B1FA2"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                    Återförsäljarinformation
                  </Typography>
                  {/* Generella ÅF-fält */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField label="Avtalsdatum" type="date"
                      value={form.resellerAgreementDate || ''}
                      onChange={e => setForm(f => ({ ...f, resellerAgreementDate: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: '1 1 160px' }} />
                    <TextField label="Täckningsområde" placeholder="t.ex. Norra Sverige"
                      value={form.resellerRegion || ''}
                      onChange={e => setForm(f => ({ ...f, resellerRegion: e.target.value }))}
                      sx={{ flex: '1 1 200px' }} />
                    <TextField label="Slutkunder (ca)" placeholder="t.ex. 40"
                      value={form.resellerEndCustomers || ''}
                      onChange={e => setForm(f => ({ ...f, resellerEndCustomers: e.target.value }))}
                      sx={{ flex: '0 1 130px' }} />
                  </Box>
                  {/* Per-produkt-avtal */}
                  <Typography variant="caption" fontWeight="bold" color="#7B1FA2"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                    Produkter &amp; avtal
                  </Typography>
                  {(form.resellerProducts || []).map((rp, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1,
                      p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e1bee7' }}>
                      <TextField select size="small" label="Produkt"
                        value={rp.productId || ''}
                        onChange={e => setForm(f => {
                          const arr = [...(f.resellerProducts || [])];
                          arr[i] = { ...arr[i], productId: e.target.value };
                          return { ...f, resellerProducts: arr };
                        })}
                        sx={{ flex: '1 1 160px' }}>
                        {allProducts.map(p => (
                          <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                      </TextField>
                      <TextField size="small" label="Avtalsnivå" placeholder="Silver, Gold…"
                        value={rp.level || ''}
                        onChange={e => setForm(f => {
                          const arr = [...(f.resellerProducts || [])];
                          arr[i] = { ...arr[i], level: e.target.value };
                          return { ...f, resellerProducts: arr };
                        })}
                        sx={{ flex: '1 1 130px' }} />
                      <TextField size="small" label="Rabatt" placeholder="t.ex. 25%"
                        value={rp.discount || ''}
                        onChange={e => setForm(f => {
                          const arr = [...(f.resellerProducts || [])];
                          arr[i] = { ...arr[i], discount: e.target.value };
                          return { ...f, resellerProducts: arr };
                        })}
                        sx={{ flex: '0 1 100px' }} />
                      <TextField size="small" label="Anteckning"
                        value={rp.notes || ''}
                        onChange={e => setForm(f => {
                          const arr = [...(f.resellerProducts || [])];
                          arr[i] = { ...arr[i], notes: e.target.value };
                          return { ...f, resellerProducts: arr };
                        })}
                        sx={{ flex: '2 1 200px' }} />
                      <IconButton size="small" color="error"
                        onClick={() => setForm(f => ({
                          ...f,
                          resellerProducts: (f.resellerProducts || []).filter((_, j) => j !== i)
                        }))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button size="small" startIcon={<AddIcon />}
                    onClick={() => setForm(f => ({
                      ...f,
                      resellerProducts: [...(f.resellerProducts || []), { productId: '', level: '', discount: '', notes: '' }]
                    }))}
                    sx={{ color: '#7B1FA2', mt: 0.5 }}>
                    Lägg till produkt
                  </Button>
                </Box>
              </Grid>
            )}

            {/* ── LEVERANTÖR (mörkgrön, visas bara när taggad) ── */}
            {form.isSupplier && (
              <Grid item xs={12}>
                <Box sx={{ bgcolor: '#f1f8f1', borderRadius: 2, p: 2, border: '1px solid #a5d6a7' }}>
                  <Typography variant="caption" fontWeight="bold" color="#1B5E20"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                    Leverantörsinformation
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField select label="Avtalsstatus"
                      value={form.supplierContractStatus || ''}
                      onChange={e => setForm(f => ({ ...f, supplierContractStatus: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: '1 1 180px' }}>
                      <MenuItem value=""><Typography color="text.disabled" variant="body2">Välj status</Typography></MenuItem>
                      {['Aktivt', 'Utgånget', 'Under omförhandling', 'Avslutat'].map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </TextField>
                    <TextField select label="Avtalstyp"
                      value={form.supplierContractType || ''}
                      onChange={e => setForm(f => ({ ...f, supplierContractType: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: '1 1 160px' }}>
                      <MenuItem value=""><Typography color="text.disabled" variant="body2">Välj typ</Typography></MenuItem>
                      {['Ramavtal', 'Löpande', 'Projekt', 'Engångsköp'].map(t => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </TextField>
                    <TextField label="Avtalsnummer" placeholder="t.ex. LV-2024-001"
                      value={form.supplierContractNumber || ''}
                      onChange={e => setForm(f => ({ ...f, supplierContractNumber: e.target.value }))}
                      sx={{ flex: '1 1 160px' }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField label="Avtalsstart" type="date"
                      value={form.supplierContractStart || ''}
                      onChange={e => setForm(f => ({ ...f, supplierContractStart: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: '1 1 150px' }} />
                    <TextField label="Avtalsslutt" type="date"
                      value={form.supplierContractEnd || ''}
                      onChange={e => setForm(f => ({ ...f, supplierContractEnd: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: '1 1 150px' }} />
                    <TextField label="Uppsägningstid" placeholder="t.ex. 3 månader"
                      value={form.supplierNoticePeriod || ''}
                      onChange={e => setForm(f => ({ ...f, supplierNoticePeriod: e.target.value }))}
                      sx={{ flex: '1 1 150px' }} />
                    <TextField label="Kontraktsvärde" placeholder="t.ex. 500 KSEK/år"
                      value={form.supplierContractValue || ''}
                      onChange={e => setForm(f => ({ ...f, supplierContractValue: e.target.value }))}
                      sx={{ flex: '1 1 150px' }} />
                    <TextField label="Betalningsvillkor" placeholder="t.ex. 30 dagar netto"
                      value={form.supplierPaymentTerms || ''}
                      onChange={e => setForm(f => ({ ...f, supplierPaymentTerms: e.target.value }))}
                      sx={{ flex: '1 1 180px' }} />
                  </Box>
                  <TextField fullWidth label="Vad de levererar"
                    placeholder="Beskriv vad företaget levererar till er – produkter, tjänster, material…"
                    multiline rows={2}
                    value={form.supplierDelivery || ''}
                    onChange={e => setForm(f => ({ ...f, supplierDelivery: e.target.value }))} />
                </Box>
              </Grid>
            )}

            {/* ── AFFÄRSINFO (blå) ── */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: '#f0f4ff', borderRadius: 2, p: 2 }}>
                <Typography variant="caption" fontWeight="bold" color="#1565C0"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                  Affärsinformation
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {/* Bransch, ägarform och kundtyp döljs (datan finns kvar) – segment och organisationstyp ersätter dem */}
                  <SegmentValjare size="medium" InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 220px' }}
                    value={form.segment || ''}
                    onChange={v => setForm(f => ({ ...f, segment: v }))} />
                  <OrganisationstypValjare size="medium" InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 200px' }}
                    value={form.organisationstyp || ''}
                    onChange={v => setForm(f => ({ ...f, organisationstyp: v }))} />
                  <TextField label="Omsättning" placeholder="t.ex. 120 MSEK"
                    value={form.revenue || ''}
                    onChange={e => setForm(f => ({ ...f, revenue: e.target.value }))}
                    sx={{ flex: '1 1 120px' }} />
                  <TextField label="Antal anställda" placeholder="t.ex. 250"
                    value={form.employees || ''}
                    onChange={e => setForm(f => ({ ...f, employees: e.target.value }))}
                    sx={{ flex: '1 1 120px' }} />
                  <TextField label="Telefon" placeholder="+46 8 123 456"
                    value={form.phone || ''}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    sx={{ flex: '1 1 130px' }} />
                </Box>
              </Box>
            </Grid>

            {/* ── ADRESS (grön) ── */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: '#f0faf4', borderRadius: 2, p: 2 }}>
                <Typography variant="caption" fontWeight="bold" color="#2E7D32"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                  Postadress
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box sx={{ flex: '2 1 220px' }}>
                    <AddressAutocomplete
                      value={form.address || ''}
                      onChange={({ address, city, postcode }) => setForm(f => ({
                        ...f, address,
                        city: city || f.city,
                        zip: postcode || f.zip,
                      }))}
                    />
                  </Box>
                  <TextField label="Postnr" value={form.zip || ''}
                    onChange={e => setForm(f => ({ ...f, zip: e.target.value }))}
                    sx={{ flex: '0 1 100px' }} />
                  <TextField label="Ort" value={form.city || ''}
                    onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    sx={{ flex: '1 1 140px' }} />
                  <TextField label="Land" placeholder="Sverige" value={form.country || ''}
                    onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                    sx={{ flex: '1 1 120px' }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="caption" fontWeight="bold" color="#2E7D32"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Besöksadress
                  </Typography>
                  <Typography variant="caption" color="text.secondary">(kartnål)</Typography>
                  {/* Kopiera postadress → besöksadress */}
                  {(form.address || form.zip || form.city) && !(form.visitStreet || form.visitZip || form.visitCity) && (
                    <Button size="small" variant="outlined"
                      onClick={() => setForm(f => ({ ...f, visitStreet: f.address, visitZip: f.zip, visitCity: f.city }))}
                      sx={{ ml: 'auto', fontSize: '0.7rem', py: 0.25, borderColor: '#2E7D32', color: '#2E7D32',
                        '&:hover': { bgcolor: '#f0faf4', borderColor: '#1B5E20' } }}>
                      Kopiera postadress hit
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '2 1 220px' }}>
                    <AddressAutocomplete
                      value={form.visitStreet || ''}
                      onChange={({ address, city, postcode }) => setForm(f => ({
                        ...f,
                        visitStreet: address,
                        visitCity: city || f.visitCity,
                        visitZip: postcode || f.visitZip,
                      }))}
                    />
                  </Box>
                  <TextField label="Postnr" value={form.visitZip || ''}
                    onChange={e => setForm(f => ({ ...f, visitZip: e.target.value }))}
                    sx={{ flex: '0 1 100px' }} />
                  <TextField label="Ort" value={form.visitCity || ''}
                    onChange={e => setForm(f => ({ ...f, visitCity: e.target.value }))}
                    sx={{ flex: '1 1 140px' }} />
                </Box>

                {/* Kartförhandsvisning */}
                {formMapCoords && (
                  <Box sx={{ mt: 1.5, borderRadius: 1.5, overflow: 'hidden', border: '1px solid #c8e6c9' }}>
                    <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#f0faf4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <LocationOnIcon sx={{ fontSize: 14, color: '#2E7D32' }} />
                        <Typography variant="caption" color="#2E7D32" fontWeight="bold">Kartförhandsvisning</Typography>
                      </Box>
                      <Tooltip title="Öppna i Google Maps">
                        <IconButton size="small" onClick={() => window.open(
                          `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([form.visitStreet || form.address, form.visitCity || form.city].filter(Boolean).join(', '))}`,
                          '_blank'
                        )}>
                          <OpenInNewIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <iframe
                      title="Kartförhandsvisning"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${formMapCoords.lon - 0.012},${formMapCoords.lat - 0.008},${formMapCoords.lon + 0.012},${formMapCoords.lat + 0.008}&layer=mapnik&marker=${formMapCoords.lat},${formMapCoords.lon}`}
                      width="100%"
                      height="200"
                      style={{ border: 0, display: 'block' }}
                    />
                  </Box>
                )}

                {/* Faktureringsadress */}
                <Typography variant="caption" fontWeight="bold" color="#2E7D32"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mt: 2, mb: 1 }}>
                  Faktureringsadress
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                  <TextField label="Gatuadress" placeholder="t.ex. Box 1234 eller Storgatan 1"
                    value={form.invoiceStreet || ''}
                    onChange={e => setForm(f => ({ ...f, invoiceStreet: e.target.value }))}
                    sx={{ flex: '2 1 220px' }} />
                  <TextField label="Postnr"
                    value={form.invoiceZip || ''}
                    onChange={e => setForm(f => ({ ...f, invoiceZip: e.target.value }))}
                    sx={{ flex: '0 1 100px' }} />
                  <TextField label="Ort"
                    value={form.invoiceCity || ''}
                    onChange={e => setForm(f => ({ ...f, invoiceCity: e.target.value }))}
                    sx={{ flex: '1 1 140px' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField label="E-post för faktura" placeholder="faktura@foretaget.se" type="email"
                    value={form.invoiceEmail || ''}
                    onChange={e => setForm(f => ({ ...f, invoiceEmail: e.target.value }))}
                    sx={{ flex: '1 1 220px' }} />
                  <TextField label="GLN / EAN" placeholder="7300000000000"
                    value={form.invoiceGLN || ''}
                    onChange={e => setForm(f => ({ ...f, invoiceGLN: e.target.value }))}
                    sx={{ flex: '1 1 160px' }} />
                  <TextField label="Peppol-ID / Operatör" placeholder="0007:5560000000"
                    value={form.invoicePeppol || ''}
                    onChange={e => setForm(f => ({ ...f, invoicePeppol: e.target.value }))}
                    sx={{ flex: '1 1 200px' }} />
                </Box>
              </Box>
            </Grid>

            {/* ── WEBB & SOCIALT (lila) ── */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: '#f5f0ff', borderRadius: 2, p: 2 }}>
                <Typography variant="caption" fontWeight="bold" color="#6a1b9a"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                  Webb & Socialt
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField label="Webbplats" placeholder="www.foretaget.se"
                    value={form.website || ''}
                    onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                    sx={{ flex: '1 1 200px' }} />
                </Box>
              </Box>
            </Grid>

          </Grid>

          {/* Notering på egen rad, full dialogbredd */}
          <Box sx={{ mt: 2 }}>
            <TextField fullWidth label="Notering / bakgrund" multiline rows={10}
              placeholder="Anteckna vad du vet om kunden, hur kontakten togs, viktiga detaljer..."
              value={form.notes || ''}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button size="large" onClick={() => { setEditing(false); setForm(company); }}>
            Avbryt
          </Button>
          <Button variant="contained" size="large" onClick={handleSaveCompany}
            disabled={saving || enrichingCompany || !form.name?.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, px: 4 }}>
            {saving ? 'Sparar...' : 'Spara ändringar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact dialog */}
      {/* ── Medarbetare från LinkedIn ──────────────────────────────────────── */}
      <Dialog open={empDialog} onClose={() => { if (!empAdding) setEmpDialog(false); }}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LinkedInIcon sx={{ color: '#0A66C2', fontSize: 28 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold">Medarbetare på LinkedIn</Typography>
              <Typography variant="caption" color="text.secondary">
                {company?.name} — välj vilka du vill lägga till som kontakter
              </Typography>
              <Typography variant="caption" sx={{ color: '#0A66C2', fontFamily: 'monospace', display: 'block', mt: 0.25, fontSize: '0.65rem' }}>
                {normalizeLinkedinUrl(company?.linkedin)}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setEmpDialog(false)} disabled={empAdding}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0, minHeight: 300 }}>
          {empLoading && empList.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
              <CircularProgress sx={{ color: '#0A66C2' }} />
              <Typography variant="body2" color="text.secondary">Hämtar medarbetare från LinkedIn…</Typography>
            </Box>
          ) : empError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{empError}</Alert>
            </Box>
          ) : empList.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>Inga medarbetare hittades.</Typography>
              <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 1 }}>
                EnrichLayer hittade inga publika medarbetare för det här företaget.
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block">
                Möjliga orsaker: företaget är litet, få anställda har länkat sitt LinkedIn-konto till sidan,
                eller så är slug-en i URL:en inte exakt den LinkedIn använder internt.
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Search + sort bar */}
              <Box sx={{ px: 2, py: 1.25, bgcolor: '#f8f9fa', borderBottom: '1px solid', borderColor: 'divider',
                display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Sök namn eller roll…"
                  value={empSearch}
                  onChange={e => setEmpSearch(e.target.value)}
                  sx={{ flex: '1 1 160px', '& .MuiInputBase-root': { fontSize: '0.8rem', height: 32 } }}
                  InputProps={{ startAdornment: (
                    <Box component="span" sx={{ mr: 0.5, color: 'text.disabled', fontSize: 16, display: 'flex' }}>
                      🔍
                    </Box>
                  )}}
                />
                <TextField
                  select size="small"
                  value={empSort}
                  onChange={e => setEmpSort(e.target.value)}
                  sx={{ width: 160, '& .MuiInputBase-root': { fontSize: '0.8rem', height: 32 } }}
                >
                  <MenuItem value="senior">Senioritet (högst först)</MenuItem>
                  <MenuItem value="name">Namn (A–Ö)</MenuItem>
                  <MenuItem value="default">Standard (API-ordning)</MenuItem>
                </TextField>
              </Box>

              {/* Select all bar */}
              <Box sx={{ px: 2.5, py: 1.25, bgcolor: '#f0f4f8', borderBottom: '1px solid', borderColor: 'divider',
                display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  onClick={() => {
                    const urls = filteredEmployees.map(e => e.profile_url);
                    if (urls.every(u => empSelected.has(u))) {
                      setEmpSelected(prev => { const s = new Set(prev); urls.forEach(u => s.delete(u)); return s; });
                    } else {
                      setEmpSelected(prev => new Set([...prev, ...urls]));
                    }
                  }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none' }}
                >
                  <Box sx={{
                    width: 18, height: 18, borderRadius: 0.5, border: '2px solid',
                    borderColor: filteredEmployees.length > 0 && filteredEmployees.every(e => empSelected.has(e.profile_url)) ? '#0A66C2' : '#bbb',
                    bgcolor: filteredEmployees.length > 0 && filteredEmployees.every(e => empSelected.has(e.profile_url)) ? '#0A66C2' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {filteredEmployees.length > 0 && filteredEmployees.every(e => empSelected.has(e.profile_url)) && (
                      <CheckIcon sx={{ fontSize: 12, color: 'white' }} />
                    )}
                  </Box>
                  <Typography variant="caption" fontWeight={600}>
                    {filteredEmployees.length > 0 && filteredEmployees.every(e => empSelected.has(e.profile_url)) ? 'Avmarkera alla' : 'Markera alla'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {empSelected.size} valda · {filteredEmployees.length}{filteredEmployees.length < empList.length ? `/${empList.length}` : ''} visade
                </Typography>
              </Box>

              {filteredEmployees.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Ingen matchar sökningen.</Typography>
                </Box>
              )}

              {filteredEmployees.map(emp => {
                // enrich_profiles:skip → emp.profile kan vara null; emp kan ha first_name/last_name/title direkt
                const p = emp.profile || {};
                const fullName = p.full_name || [emp.first_name, emp.last_name].filter(Boolean).join(' ') || '(Okänt namn)';
                const alreadyAdded = contacts.some(c => c.linkedin === emp.profile_url);
                const isSelected = empSelected.has(emp.profile_url);
                const currentExp = p.experiences?.[0];
                const title = currentExp?.title || emp.title || p.occupation || p.headline || '';
                const company2 = currentExp?.company || '';
                return (
                  <Box
                    key={emp.profile_url}
                    onClick={() => !alreadyAdded && toggleEmpSelect(emp.profile_url)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2,
                      px: 2.5, py: 1.5,
                      borderBottom: '1px solid', borderColor: 'divider',
                      bgcolor: isSelected ? '#f0f7ff' : alreadyAdded ? '#fafafa' : 'white',
                      cursor: alreadyAdded ? 'default' : 'pointer',
                      opacity: alreadyAdded ? 0.55 : 1,
                      '&:hover': !alreadyAdded ? { bgcolor: isSelected ? '#e3f0ff' : '#f5f5f5' } : {},
                    }}
                  >
                    <Box sx={{
                      width: 18, height: 18, borderRadius: 0.5, flexShrink: 0,
                      border: '2px solid', borderColor: isSelected ? '#0A66C2' : '#bbb',
                      bgcolor: isSelected ? '#0A66C2' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {isSelected && <CheckIcon sx={{ fontSize: 12, color: 'white' }} />}
                    </Box>
                    <Avatar src={p.profile_pic_url || undefined}
                      sx={{ width: 44, height: 44, bgcolor: '#0A66C2', flexShrink: 0, fontSize: '0.85rem' }}>
                      {!p.profile_pic_url && initials(fullName)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {fullName}
                        </Typography>
                        {alreadyAdded && (
                          <Chip label="Redan kontakt" size="small"
                            sx={{ height: 16, fontSize: '0.58rem', bgcolor: '#e8f5e9', color: '#2E7D32' }} />
                        )}
                      </Box>
                      {title && (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {[title, company2].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                      {p.headline && p.headline !== title && (
                        <Typography variant="caption" color="text.disabled" display="block" noWrap
                          sx={{ fontStyle: 'italic', fontSize: '0.67rem' }}>
                          {p.headline}
                        </Typography>
                      )}
                      {(p.city || p.country_full_name) && (
                        <Typography variant="caption" color="text.disabled" display="block" noWrap
                          sx={{ fontSize: '0.67rem' }}>
                          {[p.city, p.country_full_name].filter(Boolean).join(', ')}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title="Öppna LinkedIn-profil">
                      <IconButton size="small"
                        onClick={e => { e.stopPropagation(); window.open(emp.profile_url, '_blank'); }}>
                        <LinkedInIcon sx={{ fontSize: 16, color: '#0A66C2' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}

              {/* Load more */}
              {empNextPage && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Button size="small" variant="outlined"
                    disabled={empLoading}
                    onClick={() => fetchEmployees(empNextPage, false)}
                    sx={{ borderColor: '#0A66C2', color: '#0A66C2' }}>
                    {empLoading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                    Ladda fler
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            {empSelected.size > 0
              ? `${empSelected.size} person${empSelected.size > 1 ? 'er' : ''} vald${empSelected.size > 1 ? 'a' : ''}`
              : 'Klicka på en person för att välja'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setEmpDialog(false)} disabled={empAdding}>Avbryt</Button>
            <Button
              variant="contained"
              disabled={empSelected.size === 0 || empAdding}
              onClick={handleAddEmployees}
              sx={{ bgcolor: '#0A66C2', '&:hover': { bgcolor: '#084d95' }, px: 3 }}
            >
              {empAdding
                ? <><CircularProgress size={14} sx={{ mr: 1, color: 'white' }} />Lägger till…</>
                : `Lägg till ${empSelected.size > 0 ? empSelected.size + ' kontakt' + (empSelected.size > 1 ? 'er' : '') : 'valda'}`}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={contactDialog} onClose={() => setContactDialog(false)}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PersonIcon sx={{ color: '#FF6B00', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {editingContactId ? 'Redigera kontakt' : 'Ny kontakt'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Fyll i det du vet – du kan komplettera senare
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* ── Klistra från Outlook/signatur ── */}
            <Box sx={{ bgcolor: '#fff8e1', borderRadius: 2, p: 2, border: '1px solid #ffe082' }}>
              <Typography variant="caption" fontWeight="bold"
                sx={{ color: '#E65100', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Snabbfyll — klistra från Outlook eller e-postsignatur
              </Typography>
              <TextField fullWidth size="small" multiline minRows={1}
                placeholder='Klistra in bokningsmail, Outlook-mottagare eller e-postsignatur'
                value={contactPaste}
                onChange={e => {
                  const val = e.target.value;
                  setContactPaste(val);
                  const booking = parseBookingEmail(val);
                  if (booking) {
                    setParsedCompanyFromPaste(booking);
                    setContactForm(f => ({
                      ...f,
                      ...(booking.contactName && !f.name ? { name: booking.contactName } : {}),
                      ...(booking.contactRole && !f.role ? { role: booking.contactRole } : {}),
                      ...(booking.contactEmail && !f.email ? { email: booking.contactEmail } : {}),
                      ...(booking.contactPhone && !f.phone ? { phone: booking.contactPhone } : {}),
                      // parseBookingEmail slår bara till på Comdevas bokningsmail, så
                      // härkomsten är känd här och behöver inte skrivas in för hand.
                      ...(!f.dealRelation ? { dealRelation: COMDEVA_RELATION } : {}),
                    }));
                  } else {
                    setParsedCompanyFromPaste(null);
                    const parsed = parseContactFromPaste(val);
                    if (parsed) {
                      setContactForm(f => ({
                        ...f,
                        ...(parsed.name && !f.name ? { name: parsed.name } : {}),
                        ...(parsed.email && !f.email ? { email: parsed.email } : {}),
                        ...(parsed.phone && !f.phone ? { phone: parsed.phone } : {}),
                      }));
                    }
                    // Inklistrad text som inte är ett bokningsmail tolkas som
                    // mailsignatur med AI; inskriven text tolkas på knapptryck.
                    if (e.nativeEvent?.inputType === 'insertFromPaste' && val.trim().length > 10) {
                      tolkaSignatur(val);
                    }
                  }
                }}
                disabled={signatur.status === 'loading'}
                helperText="Bokningsmail och Outlook-mottagare fylls i direkt. En mailsignatur tolkas med AI och fyller kontakten – och kundens tomma fält om du vill."
                InputProps={{ sx: { bgcolor: 'white' } }}
              />
              {contactPaste.trim() && !parseBookingEmail(contactPaste) && signatur.status !== 'loading' && (
                <Button size="small" startIcon={<AutoFixHighIcon />} onClick={() => tolkaSignatur(contactPaste)}
                  sx={{ mt: 1, color: '#7E57C2' }}>
                  {signatur.status === 'done' ? 'Tolka signaturen igen' : 'Tolka som mailsignatur (AI)'}
                </Button>
              )}
              <SignaturStatus sig={signatur} kund={company} />
            </Box>

            {/* ── Företagsinfo från bokningsmail ── */}
            {parsedCompanyFromPaste && (parsedCompanyFromPaste.orgNumber || parsedCompanyFromPaste.city || parsedCompanyFromPaste.employees || parsedCompanyFromPaste.revenue) && (
              <Box sx={{ bgcolor: '#e8f5e9', borderRadius: 2, p: 2, border: '1px solid #a5d6a7' }}>
                <Typography variant="caption" fontWeight="bold"
                  sx={{ color: '#2E7D32', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Företagsinfo hittad i bokningsmailet
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                  {parsedCompanyFromPaste.companyName && <Chip size="small" label={parsedCompanyFromPaste.companyName} sx={{ bgcolor: '#c8e6c9' }} />}
                  {parsedCompanyFromPaste.orgNumber && <Chip size="small" label={`Org.nr: ${parsedCompanyFromPaste.orgNumber}`} />}
                  {parsedCompanyFromPaste.city && <Chip size="small" label={`Ort: ${parsedCompanyFromPaste.city}`} />}
                  {parsedCompanyFromPaste.employees && <Chip size="small" label={`Anst: ${parsedCompanyFromPaste.employees}`} />}
                  {parsedCompanyFromPaste.industry && <Chip size="small" label={parsedCompanyFromPaste.industry} />}
                  {parsedCompanyFromPaste.revenue && <Chip size="small" label={`Oms: ${Number(parsedCompanyFromPaste.revenue).toLocaleString('sv-SE')} kr`} />}
                </Box>
                <Button size="small" variant="outlined"
                  sx={{ borderColor: '#2E7D32', color: '#2E7D32', '&:hover': { bgcolor: '#e8f5e9' } }}
                  onClick={async () => {
                    const updates = {};
                    if (parsedCompanyFromPaste.orgNumber && !company?.orgNumber) updates.orgNumber = parsedCompanyFromPaste.orgNumber;
                    if (parsedCompanyFromPaste.city && !company?.city) updates.city = parsedCompanyFromPaste.city;
                    if (parsedCompanyFromPaste.employees && !company?.employees) updates.employees = parsedCompanyFromPaste.employees;
                    if (parsedCompanyFromPaste.industry && !company?.industry) updates.industry = parsedCompanyFromPaste.industry;
                    if (parsedCompanyFromPaste.revenue && !company?.revenue) updates.revenue = parsedCompanyFromPaste.revenue;
                    if (Object.keys(updates).length > 0) {
                      await updateDoc(doc(db, 'crmCompanies', companyId), { ...updates, updatedAt: new Date() });
                      setCompany(c => ({ ...c, ...updates }));
                    }
                    setParsedCompanyFromPaste(null);
                  }}>
                  Fyll i tomma fält på företaget
                </Button>
              </Box>
            )}

            {/* ── LinkedIn (överst) ── */}
            <Box sx={{ bgcolor: 'rgba(0,119,181,0.06)', borderRadius: 2, p: 2, border: '1px solid rgba(0,119,181,0.15)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <AutoFixHighIcon sx={{ fontSize: 15, color: '#0077b5', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: '#0077b5', lineHeight: 1.4 }}>
                  Fyll i LinkedIn-profil och klicka ✨ för att automatiskt hämta namn, roll, e-post, plats, utbildning och mer.
                </Typography>
              </Box>
              <TextField fullWidth size="small" label="LinkedIn-URL" autoFocus
                placeholder="https://www.linkedin.com/in/..."
                value={contactForm.linkedin}
                onChange={e => { setContactForm(f => ({ ...f, linkedin: e.target.value })); setEnrichContactMsg(''); }}
                InputProps={{
                  endAdornment: contactForm.linkedin && (
                    <Tooltip title="Hämta kontaktinfo från LinkedIn">
                      <IconButton size="small" onClick={handleContactEnrich} disabled={enrichingContact}
                        sx={{ borderRadius: 1, p: '3px', color: '#0077b5', '&:hover': { bgcolor: 'rgba(0,119,181,0.1)' } }}>
                        {enrichingContact ? <CircularProgress size={14} /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                  )
                }} />
              {enrichContactMsg && (
                <Alert severity={enrichContactMsg.startsWith('Berikade') ? 'success' : 'error'}
                  sx={{ mt: 0.75, py: 0 }} onClose={() => setEnrichContactMsg('')}>
                  <Typography variant="caption">{enrichContactMsg}</Typography>
                </Alert>
              )}
            </Box>

            {/* ── Grunduppgifter ── */}
            <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                Grunduppgifter
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField fullWidth size="small" label="Namn *"
                  value={contactForm.name}
                  onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))} />
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <TextField fullWidth size="small" label="Roll / Befattning"
                    placeholder="t.ex. VD, Projektchef, IT-chef"
                    value={contactForm.role}
                    onChange={e => setContactForm(f => ({ ...f, role: e.target.value }))} />
                  <TextField fullWidth size="small" label="Avdelning"
                    value={contactForm.department || ''}
                    onChange={e => setContactForm(f => ({ ...f, department: e.target.value }))} />
                  <TextField fullWidth size="small" label="Relation till affär"
                    placeholder="t.ex. Beslutsfattare, Ekonomiansvarig"
                    value={contactForm.dealRelation}
                    onChange={e => setContactForm(f => ({ ...f, dealRelation: e.target.value }))} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <TextField fullWidth size="small" label="E-post" type="email"
                    placeholder="namn@foretaget.se"
                    value={contactForm.email}
                    onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} />
                  <TextField fullWidth size="small" label="Mobil"
                    placeholder="+46 70 123 45 67"
                    value={contactForm.mobile || ''}
                    onChange={e => setContactForm(f => ({ ...f, mobile: e.target.value }))} />
                  <TextField fullWidth size="small" label="Direktnummer"
                    placeholder="+46 8 123 456 78"
                    value={contactForm.phone}
                    onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))} />
                </Box>
              </Box>
            </Box>

            {/* ── LinkedIn-berikade fält ── */}
            {(contactForm.location || contactForm.summary || contactForm.currentCompany || contactForm.education || contactForm.languages || contactForm.photoUrl) && (
              <Box sx={{ bgcolor: 'rgba(0,119,181,0.05)', borderRadius: 2, p: 2, border: '1px solid rgba(0,119,181,0.15)' }}>
                <Typography variant="caption" sx={{ color: '#0077b5', fontWeight: 700, display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  LinkedIn-data
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {contactForm.photoUrl && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={contactForm.photoUrl} sx={{ width: 48, height: 48 }}>
                        {initials(contactForm.name)}
                      </Avatar>
                      <TextField fullWidth size="small" label="Profilbild-URL"
                        value={contactForm.photoUrl}
                        onChange={e => setContactForm(f => ({ ...f, photoUrl: e.target.value }))} />
                    </Box>
                  )}
                  {contactForm.summary && (
                    <TextField fullWidth size="small" label="LinkedIn-rubrik"
                      value={contactForm.summary}
                      onChange={e => setContactForm(f => ({ ...f, summary: e.target.value }))} />
                  )}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {contactForm.location && (
                      <TextField fullWidth size="small" label="Plats / Stad"
                        value={contactForm.location}
                        onChange={e => setContactForm(f => ({ ...f, location: e.target.value }))} />
                    )}
                    {contactForm.currentCompany && (
                      <TextField fullWidth size="small" label="Nuvarande arbetsgivare"
                        value={contactForm.currentCompany}
                        onChange={e => setContactForm(f => ({ ...f, currentCompany: e.target.value }))} />
                    )}
                  </Box>
                  {contactForm.education && (
                    <TextField fullWidth size="small" label="Utbildning"
                      value={contactForm.education}
                      onChange={e => setContactForm(f => ({ ...f, education: e.target.value }))} />
                  )}
                  {contactForm.languages && (
                    <TextField fullWidth size="small" label="Språk"
                      value={contactForm.languages}
                      onChange={e => setContactForm(f => ({ ...f, languages: e.target.value }))} />
                  )}
                </Box>
              </Box>
            )}

            {/* ── Anteckningar + primär ── */}
            <TextField fullWidth size="small" label="Personliga anteckningar"
              multiline rows={3}
              placeholder="Intressen, preferenser, vad ni pratat om senast, viktigt att komma ihåg..."
              value={contactForm.notes}
              onChange={e => setContactForm(f => ({ ...f, notes: e.target.value }))} />

            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              p: 1.5, borderRadius: 2,
              border: '1px solid', borderColor: contactForm.primary ? '#1565C0' : 'divider',
              bgcolor: contactForm.primary ? '#E3F2FD' : 'transparent',
              transition: 'all 0.2s',
            }}>
              <Box>
                <Typography variant="body2" fontWeight={contactForm.primary ? 700 : 400}>
                  Primär kontakt
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Visas som huvudkontakt för detta företag
                </Typography>
              </Box>
              <Switch
                checked={!!contactForm.primary}
                onChange={e => setContactForm(f => ({ ...f, primary: e.target.checked }))}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#1565C0' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#1565C0' },
                  }}
                />
              </Box>

          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button onClick={() => setContactDialog(false)} size="large">Avbryt</Button>
          <Button variant="contained" onClick={handleSaveContact} size="large"
            disabled={saving || enrichingContact || !contactForm.name.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, px: 4 }}>
            {saving ? 'Sparar...' : 'Spara kontakt'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Referensdialog */}
      <Dialog open={refDialog} onClose={() => setRefDialog(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <BusinessIcon sx={{ color: '#FF6B00', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {editingRefId ? 'Redigera referensuppdrag' : 'Nytt referensuppdrag'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Beskrivning av tidigare uppdrag hos kunden
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Uppdragsnamn *" autoFocus
                value={refForm.name}
                onChange={e => setRefForm(f => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Budget / Kontraktsvärde"
                placeholder="t.ex. 2,5 MSEK"
                value={refForm.budget}
                onChange={e => setRefForm(f => ({ ...f, budget: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Vår roll"
                placeholder="t.ex. Teknisk konsult, Projektledare"
                value={refForm.role}
                onChange={e => setRefForm(f => ({ ...f, role: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Avdelning" select
                value={refForm.departmentId}
                onChange={e => setRefForm(f => ({ ...f, departmentId: e.target.value }))}>
                <MenuItem value="">– Välj avdelning –</MenuItem>
                {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Startdatum" placeholder="YYYY-MM"
                value={refForm.startDate}
                onChange={e => setRefForm(f => ({ ...f, startDate: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Slutdatum" placeholder="YYYY-MM eller Pågående"
                value={refForm.endDate}
                onChange={e => setRefForm(f => ({ ...f, endDate: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Uppdragsbeskrivning" multiline rows={4}
                placeholder="Beskriv uppdraget och vad det handlade om..."
                value={refForm.projectDescription}
                onChange={e => setRefForm(f => ({ ...f, projectDescription: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Vår uppgift" multiline rows={3}
                placeholder="Vad vi konkret levererade och bidrog med..."
                value={refForm.ourAssignment}
                onChange={e => setRefForm(f => ({ ...f, ourAssignment: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                Nyckelord
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7, mb: 1 }}>
                {refForm.keywords.map((k, i) => (
                  <Chip key={i} label={k} size="small" onDelete={() =>
                    setRefForm(f => ({ ...f, keywords: f.keywords.filter((_, j) => j !== i) }))} />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField size="small" placeholder="Skriv nyckelord och tryck Enter"
                  value={refTagInput} onChange={e => setRefTagInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && refTagInput.trim()) {
                      setRefForm(f => ({ ...f, keywords: [...f.keywords, refTagInput.trim()] }));
                      setRefTagInput('');
                    }
                  }} sx={{ flex: 1 }} />
                <Button onClick={() => {
                  if (refTagInput.trim()) {
                    setRefForm(f => ({ ...f, keywords: [...f.keywords, refTagInput.trim()] }));
                    setRefTagInput('');
                  }
                }}>Lägg till</Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button onClick={() => setRefDialog(false)} size="large">Avbryt</Button>
          <Button variant="contained" onClick={handleSaveRef} size="large"
            disabled={saving || !refForm.name.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, px: 4 }}>
            {saving ? 'Sparar...' : 'Spara referensuppdrag'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk e-postimport */}
      <Dialog open={bulkDialog} onClose={() => setBulkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <AddIcon sx={{ color: '#FF6B00' }} />
          Klistra in e-postlista
          <IconButton size="small" onClick={() => setBulkDialog(false)} sx={{ ml: 'auto' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Klistra in mottagare från Outlook (semikolonseparerat). Namn och e-post hämtas automatiskt.
          </Typography>
          <TextField
            multiline minRows={4} maxRows={10} fullWidth size="small"
            placeholder={`"david.kummel@polisen.se" <david.kummel@polisen.se>; "mauricio.caballero@polisen.se" <mauricio.caballero@polisen.se>`}
            value={bulkText}
            onChange={e => {
              setBulkText(e.target.value);
              setBulkParsed(parseOutlookEmailList(e.target.value));
            }}
            sx={{ mb: 2, fontFamily: 'monospace', fontSize: '0.78rem' }}
          />
          {bulkParsed.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                {bulkParsed.length} kontakter identifierade:
              </Typography>
              <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
                {bulkParsed.map((c, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 0.75,
                    borderBottom: i < bulkParsed.length - 1 ? '1px solid #f0f0f0' : 'none',
                    alignItems: 'center' }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', bgcolor: '#FF6B00' }}>
                      {initials(c.name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{c.name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{c.email}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {bulkText.trim() && bulkParsed.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>Inga e-postadresser hittades i texten.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBulkDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleBulkAdd}
            disabled={bulkAdding || bulkParsed.length === 0}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {bulkAdding ? 'Lägger till...' : `Lägg till ${bulkParsed.length || ''} kontakter`}
          </Button>
        </DialogActions>
      </Dialog>

      <CopyCustomerDataDialog
        open={copyCustomerOpen}
        onClose={() => setCopyCustomerOpen(false)}
        company={company}
        contacts={contacts}
      />

      {/* Berikning när org.nr fyllts i – bara tomma fält */}
      <Dialog open={!!berikning} onClose={() => setBerikning(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Hämta uppgifter från Bolagsverket?</DialogTitle>
        <DialogContent>
          {berikning?.steg === 'fraga' && (
            <Typography variant="body2">
              {company?.name} har fått org.nr {company?.orgNumber}. Uppgifterna hämtas från Bolagsverket och
              fyller bara i fält som är tomma – inget befintligt skrivs över.
            </Typography>
          )}
          {berikning?.steg === 'hamtar' && <CircularProgress size={22} />}
          {berikning?.steg === 'fel' && <Alert severity="error">Kunde inte hämta: {berikning.fel}</Alert>}
          {berikning?.steg === 'visa' && (Object.keys(berikning.falt).length ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Följande tomma fält fylls i:</Typography>
              {Object.entries(berikning.falt).map(([k, v]) => (
                <Box key={k} sx={{ display: 'flex', gap: 1, py: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 120, flexShrink: 0 }}>{k}</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {k === 'sniCodes' ? JSON.parse(v).map(s => s.label).join(', ') : String(v)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : <Typography variant="body2">Bolagsverket har inga uppgifter för fält som är tomma på kunden.</Typography>)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBerikning(null)}>{berikning?.steg === 'fraga' ? 'Inte nu' : 'Stäng'}</Button>
          {berikning?.steg === 'fraga' && <Button variant="contained" onClick={hamtaBerikning}>Hämta</Button>}
          {berikning?.steg === 'visa' && Object.keys(berikning.falt).length > 0 && (
            <Button variant="contained" onClick={sparaBerikning}>Fyll i {Object.keys(berikning.falt).length} fält</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
